import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';

// lucide‑react icons
import {
  ChevronLeft,
  ChevronRight,
  House,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  Download,
  FileDown,
  FileText,
  Pause,
  Plus,
  Maximize2,
  Save,
  Settings,
  SkipBack,
  SkipForward,
  Upload,
  Users,
  Video,
  X,
  AlertTriangle,
  Shield,
  Timer,
  Lock
} from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';

// app‑specific hooks / helpers
import { useAssessment, DEFAULT_LATE_POLICY } from './SharedContext';
import { describeLatePolicy } from '../utils/latePolicy';
import { ensureUniqueCriterionIds } from '../utils/ensureUniqueCriterionIds';
import { getSchoolLogo, generateReportHeader, getReportStyles } from './logoIntegrationUtility';
import { calculateLevelPoints, calculateRubricFinalScore, getValidPointRange } from '../utils/rubricCalculations';
import { computeGrade } from '../utils/computeGrade';

const parseHexColor = (color) => {
  if (!color || typeof color !== 'string') return null;

  const hex = color.replace('#', '');
  if (hex.length !== 3 && hex.length !== 6) return null;

  const fullHex = hex.length === 3
    ? hex.split('').map(char => char + char).join('')
    : hex;

  const r = parseInt(fullHex.slice(0, 2), 16);
  const g = parseInt(fullHex.slice(2, 4), 16);
  const b = parseInt(fullHex.slice(4, 6), 16);

  if ([r, g, b].some(value => Number.isNaN(value))) return null;

  return { r, g, b };
};

const getContrastingTextColor = (backgroundColor) => {
  const rgb = parseHexColor(backgroundColor);

  if (!rgb) {
    return '#111827';
  }

  const brightness = ((rgb.r * 299) + (rgb.g * 587) + (rgb.b * 114)) / 1000;
  return brightness > 160 ? '#111827' : '#ffffff';
};

const getLevelBadgeColors = (color) => {
  const background = color || '#6b7280';
  const text = getContrastingTextColor(background);

  return { background, text };
};

// TipTap MenuBar (from AIRubricPromptGenerator.js)
const MenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const addImage = () => {
    const url = window.prompt('Image URL');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  return (
    <div className="p-2 border-b border-gray-300 bg-gray-50 flex items-center flex-wrap gap-x-2 gap-y-1 text-sm">
      <button title="Undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>Undo</button>
      <button title="Redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>Redo</button>
      <div className="w-[1px] h-5 bg-gray-300 mx-1" />
      <button onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'is-active' : ''} title="Bold"><b>B</b></button>
      <button onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'is-active' : ''} title="Italic"><i>I</i></button>
      <button onClick={() => editor.chain().focus().toggleUnderline().run()} className={editor.isActive('underline') ? 'is-active' : ''} title="Underline"><u>U</u></button>
      <button onClick={() => editor.chain().focus().toggleStrike().run()} className={editor.isActive('strike') ? 'is-active' : ''} title="Strikethrough"><s>S</s></button>
      <div className="w-[1px] h-5 bg-gray-300 mx-1" />
      <button onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''} title="Heading 1">H1</button>
      <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''} title="Heading 2">H2</button>
      <button onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''} title="Heading 3">H3</button>
      <div className="w-[1px] h-5 bg-gray-300 mx-1" />
      <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'is-active' : ''} title="Bullet List">• List</button>
      <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor.isActive('orderedList') ? 'is-active' : ''} title="Numbered List">1. List</button>
      <div className="w-[1px] h-5 bg-gray-300 mx-1" />
      <button onClick={() => editor.chain().focus().setTextAlign('left').run()} className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''} title="Align Left">Left</button>
      <button onClick={() => editor.chain().focus().setTextAlign('center').run()} className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''} title="Align Center">Center</button>
      <button onClick={() => editor.chain().focus().setTextAlign('right').run()} className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''} title="Align Right">Right</button>
      <div className="w-[1px] h-5 bg-gray-300 mx-1" />
      <button onClick={setLink} className={editor.isActive('link') ? 'is-active' : ''} title="Add Link">Link</button>
      <button onClick={addImage} title="Add Image">Image</button>
      <style jsx>{`
                button { padding: 0.25rem 0.5rem; border-radius: 4px; transition: background-color 0.2s; }
                button:hover { background-color: #e5e7eb; }
                button.is-active { background-color: #dbeafe; color: #1d4ed8; font-weight: bold; }
                button:disabled { opacity: 0.5; cursor: not-allowed; }
            `}</style>
    </div>
  );
};

const TiptapEditor = React.forwardRef(({ value, onChange, placeholder }, ref) => {
  const editor = useEditor({
    extensions: [StarterKit, Underline, Image, Link.configure({ openOnClick: false }), TextAlign.configure({ types: ['heading', 'paragraph'] })],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'min-h-[200px] p-4 focus:outline-none',
      },
    },
  });

  React.useImperativeHandle(ref, () => editor, [editor]);

  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      const isSame = editor.getHTML() === value;
      if (!isSame) {
        editor.commands.setContent(value, false);
      }
    }
  }, [value, editor]);

  return (
    <div className="rich-text-editor border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all duration-200">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} placeholder={placeholder} />
      <style>{`
                .ProseMirror p.is-editor-empty:first-child::before { content: attr(data-placeholder); float: left; color: #adb5bd; pointer-events: none; height: 0; }
                .ProseMirror h1 { font-size: 1.75em; font-weight: bold; }
                .ProseMirror h2 { font-size: 1.5em; font-weight: bold; }
                .ProseMirror h3 { font-size: 1.25em; font-weight: bold; }
                .ProseMirror ul { list-style-type: disc; padding-left: 2rem; }
                .ProseMirror ol { list-style-type: decimal; padding-left: 2rem; }
                .ProseMirror img { max-width: 100%; height: auto; display: block; margin-left: auto; margin-right: auto; }
                .ProseMirror a { color: #2563eb; text-decoration: underline; cursor: pointer; }
            `}</style>
    </div>
  );
});
TiptapEditor.displayName = 'TiptapEditor';

/**
 * PRIVACY-FOCUSED GradingTemplate - Session-Only Storage
 * PRESERVES ALL ORIGINAL FUNCTIONALITY while implementing privacy controls
 */

const GradingTemplate = () => {
  // Get shared context - ENHANCED with session management
  const {
    sharedRubric,
    setSharedRubric,
    sharedCourseDetails,
    setSharedCourseDetails,
    gradingData: sharedGradingData,
    setGradingData: setSharedGradingData,
    classList,
    gradingSession,
    nextStudentInSession,
    previousStudentInSession,
    updateGradingSession,
    setActiveTab,
    currentStudent,
    saveDraft,
    loadDraft,
    saveFinalGrade,
    loadFinalGrade,
    getGradeStatus,
    currentLatePolicy,
    courseLatePolicies,
    getEffectiveLatePolicy,
    applyLatePolicyToScore,
    gradingResetToken,
    // PRIVACY: Session management
    sessionActive,
    sessionManager,
    sessionExpirationEnabled,
    extendSession,
    gradeBook,
    setGradeBook,
    // Assignment prompt data for date auto-population
    assignmentPromptFormData
  } = useAssessment();

  const getLetterGradeFromPercentage = (percentage) => {
    const programType = classList?.courseMetadata?.programType || 'Degree';
    return computeGrade({ programType, percent: percentage }).letter;
  };

  const normalizeAssignmentName = (value = '') => value.replace(/[^a-z0-9]/gi, '').toLowerCase();

  const PROJECT_SELECTION_KEY = 'gradingpilot.selectedProjectId';

  const readStoredProjectId = useCallback(() => {
    try {
      return sessionStorage.getItem(PROJECT_SELECTION_KEY);
    } catch (error) {
      console.warn('Unable to read stored project selection', error);
      return null;
    }
  }, []);

  const writeStoredProjectId = useCallback((projectId) => {
    try {
      if (projectId) {
        sessionStorage.setItem(PROJECT_SELECTION_KEY, projectId);
      } else {
        sessionStorage.removeItem(PROJECT_SELECTION_KEY);
      }
    } catch (error) {
      console.warn('Unable to persist project selection', error);
    }
  }, []);

  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    const stored = readStoredProjectId();
    const preferredId = gradeBook?.metadata?.selectedProjectId || stored;
    return preferredId && gradeBook?.projects?.some((p) => p.id === preferredId) ? preferredId : null;
  });
  const [inlineEditor, setInlineEditor] = useState({ show: false, content: '', field: null, onSave: null });
  const richTextContentRef = useRef('');
  const editorRef = useRef(null);

  useEffect(() => {
    // Debug: observe gradeBook availability during navigation/remounts
    console.debug('GradeBook project effect', {
      hasGradeBook: Boolean(gradeBook),
      projectCount: gradeBook?.projects?.length || 0
    });

    if (!gradeBook || !gradeBook.projects?.length) {
      // Keep existing selection during transient unloads; clear only on explicit invalidation below
      return;
    }

    const storedSelection = gradeBook.metadata?.selectedProjectId;
    const persistedSelection =
      selectedProjectId || readStoredProjectId() || storedSelection;

    if (
      persistedSelection &&
      !gradeBook.projects.some((p) => p.id === persistedSelection)
    ) {
      setSelectedProjectId(null);
      writeStoredProjectId(null);

      if (storedSelection && setGradeBook) {
        setGradeBook({
          ...gradeBook,
          metadata: {
            ...gradeBook.metadata,
            selectedProjectId: null
          }
        });
      }

      return;
    }

    if (persistedSelection) {
      setSelectedProjectId(persistedSelection);

      if (storedSelection !== persistedSelection && setGradeBook) {
        setGradeBook({
          ...gradeBook,
          metadata: {
            ...gradeBook.metadata,
            selectedProjectId: persistedSelection
          }
        });
      }

      writeStoredProjectId(persistedSelection);
    }
  }, [gradeBook, selectedProjectId, readStoredProjectId, writeStoredProjectId, setGradeBook]);

  const updateSelectedProject = (projectId) => {
    setSelectedProjectId(projectId || null);
    writeStoredProjectId(projectId || null);

    if (gradeBook && setGradeBook) {
      setGradeBook({
        ...gradeBook,
        metadata: {
          ...gradeBook.metadata,
          selectedProjectId: projectId || null
        }
      });
    }
  };

  const getAssignmentColumnName = () => {
    return (
      sharedCourseDetails?.assignment?.title ||
      sharedCourseDetails?.assignment?.name ||
      sharedRubric?.assignmentInfo?.title ||
      gradingData.assignment?.title ||
      gradingData.assignment?.name ||
      sharedRubric?.assignmentInfo?.name ||
      ''
    ).trim();
  };

  const computeFinalPercentage = (rawScore, totalPossible) => {
    if (!Number.isFinite(rawScore) || !Number.isFinite(totalPossible) || totalPossible <= 0) return 0;
    return Math.round((rawScore / totalPossible) * 100);
  };

  const syncGradeToGradeBook = (studentId, totalScoreData) => {
    if (!gradeBook || !setGradeBook) return;
    if (!gradeBook.projects?.length || !gradeBook.students?.length) return;

    const assignmentName = getAssignmentColumnName();
    if (!assignmentName) return;

    const normalizedAssignment = normalizeAssignmentName(assignmentName);

    let targetProject = null;

    if (selectedProjectId) {
      targetProject = gradeBook.projects.find((project) => project.id === selectedProjectId);
    }

    if (!targetProject) {
      targetProject = gradeBook.projects.find((project) => {
        const normalizedProjectName = normalizeAssignmentName(project.name || '');
        return (
          normalizedProjectName === normalizedAssignment ||
          normalizedProjectName.includes(normalizedAssignment) ||
          normalizedAssignment.includes(normalizedProjectName)
        );
      });
    }

    if (!targetProject) {
      console.warn(`No gradebook column found for assignment "${assignmentName}"`);
      return;
    }

    const studentExists = gradeBook.students.some((student) => student.id === studentId);
    if (!studentExists) {
      console.warn(`Student ${studentId} not found in loaded gradebook. Skipping sync.`);
      return;
    }

    const rawScore = Number.isFinite(totalScoreData?.rawScore)
      ? totalScoreData.rawScore
      : totalScoreData?.finalScore;
    if (rawScore === null || rawScore === undefined) return;

    const adjustedScore = Number.isFinite(totalScoreData?.finalScore)
      ? totalScoreData.finalScore
      : rawScore;
    const totalPossible = targetProject.maxPoints
      || totalScoreData?.totalMaxPoints
      || sharedRubric?.assignmentInfo?.totalPoints
      || 100;
    const percentage = Number.isFinite(totalScoreData?.finalPercentage)
      ? totalScoreData.finalPercentage
      : computeFinalPercentage(adjustedScore, totalPossible);
    const letterGrade = getLetterGradeFromPercentage(percentage);
    const timestamp = new Date().toISOString();

    const updatedGrades = {
      ...(gradeBook.grades || {}),
      [studentId]: {
        ...((gradeBook.grades && gradeBook.grades[studentId]) || {}),
        [targetProject.id]: {
          rawScore: parseFloat(rawScore.toFixed(1)),
          adjustedScore: Number.isFinite(adjustedScore) ? parseFloat(adjustedScore.toFixed(1)) : undefined,
          percentage,
          letterGrade,
          lastModified: timestamp
        }
      }
    };

    setGradeBook({
      ...gradeBook,
      grades: updatedGrades,
      metadata: {
        ...gradeBook.metadata,
        lastModified: timestamp
      }
    });

    console.log(`✅ Synced grade for ${studentId} to gradebook column "${targetProject.name}"`);
  };

  // PRIVACY: Session monitoring state
  const [sessionWarning, setSessionWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);

  // Helper function to convert HTML content to readable text format
  const renderFormattedContent = (htmlContent, { trim = true } = {}) => {
    if (!htmlContent) return '';

    // Convert basic HTML to readable text format
    let textContent = htmlContent
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<p[^>]*>/gi, '')
      .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')
      .replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**')
      .replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*')
      .replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*')
      .replace(/<u[^>]*>(.*?)<\/u>/gi, '_$1_')
      .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n')
      .replace(/<li[^>]*>(.*?)<\/li>/gi, '• $1\n')
      .replace(/<\/ul>/gi, '\n')
      .replace(/<ul[^>]*>/gi, '')
      .replace(/<\/ol>/gi, '\n')
      .replace(/<ol[^>]*>/gi, '')
      .replace(/<[^>]*>/g, '') // Remove any remaining HTML tags
      .replace(/\n\s*\n/g, '\n'); // Remove extra line breaks

    return trim ? textContent.trim() : textContent;
  };

  const [aiFeedbackPrompt, setAIFeedbackPrompt] = useState('');
  const rubricResetTokenRef = useRef(null);
  const rubricPersistTimeoutRef = useRef(null);
  const autosaveTimeoutRef = useRef(null);

  // Refs to track latest values for unmount cleanup (safety net)
  const latestGradingDataRef = useRef(null);
  const latestStudentRef = useRef(currentStudent);
  const saveDraftRef = useRef(saveDraft);

  const persistDraftForCurrentStudent = useCallback((gradingDataToPersist) => {
    if (!currentStudent?.id) return;
    saveDraft(currentStudent.id, gradingDataToPersist);
  }, [currentStudent?.id, saveDraft]);

  const copyAIFeedbackPromptToClipboard = useCallback(async () => {
    if (!aiFeedbackPrompt) return;
    try {
      await navigator.clipboard.writeText(aiFeedbackPrompt);
      alert('AI feedback prompt copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy AI feedback prompt:', error);
      alert('Unable to copy prompt. Please try again.');
    }
  }, [aiFeedbackPrompt]);

  // State for all grading data - initialize from shared context or defaults
  const [localGradingData, setLocalGradingData] = useState(() => {
    // 1) Try loading a saved draft first
    if (currentStudent?.id) {
      const draft = loadDraft(currentStudent.id);
      if (draft) {
        return draft;
      }
    }
    // 2) If rubric was just transferred, use that
    if (sharedGradingData) {
      return {
        ...sharedGradingData,
        student: currentStudent || sharedGradingData.student || { name: '', id: '', email: '' }
      };
    }
    // 3) Otherwise, start fresh for this student
    return {
      student: currentStudent || { name: '', id: '', email: '' },
      course: sharedCourseDetails?.course || { code: '', name: '', instructor: '', evaluator: '', term: '' },
      assignment: sharedCourseDetails?.assignment || { name: '', dueDate: '', dueAt: '', maxPoints: 100 },
      feedback: { general: '', strengths: '', improvements: '' },
      attachments: [],
      videoLinks: [],
      latePolicy: { penaltyApplied: false, penaltyPercentage: 0, daysLate: 0 },
      metadata: {
        gradedBy: '',
        gradedDate: '',
        aiAssisted: false,
        rubricIntegrated: false,
        submittedAt: '',
        hasApprovedExtension: false,
        lateOverrideDays: null
      },
      rubricGrading: {}
    };
  });

  // Use local grading data as the main state - THIS IS NOW THE SINGLE SOURCE OF TRUTH
  const gradingData = localGradingData;
  const setGradingData = setLocalGradingData;

  // Keep refs current for unmount cleanup
  useEffect(() => { latestGradingDataRef.current = localGradingData; }, [localGradingData]);
  useEffect(() => { latestStudentRef.current = currentStudent; }, [currentStudent]);
  useEffect(() => { saveDraftRef.current = saveDraft; }, [saveDraft]);

  // Safety net: flush all pending data to SharedContext on unmount.
  // The primary protection is CSS display:none in App.js (prevents unmount),
  // but this ensures no data loss if the component IS unmounted (e.g. session clear).
  useEffect(() => {
    return () => {
      const data = latestGradingDataRef.current;
      const student = latestStudentRef.current;

      // Flush to SharedContext so data survives even without a draft
      if (data) {
        setSharedGradingData(data);
        setSharedCourseDetails({
          student: data.student,
          course: data.course,
          assignment: data.assignment
        });
      }

      // Save draft for current student
      if (student?.id && data) {
        saveDraftRef.current(student.id, data);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rubric-specific state
  const [showRubricComments, setShowRubricComments] = useState({});

  // UI State for new layout
  const [showLeftSidebar, setShowLeftSidebar] = useState(true);
  const [expandedCriteria, setExpandedCriteria] = useState({});

  // Calculate completion percentage
  const completionPercentage = useMemo(() => {
    if (!sharedRubric?.criteria) return 0;
    const completed = sharedRubric.criteria.filter(
      (c) => gradingData.rubricGrading[c.id]?.selectedLevel
    ).length;
    return Math.round((completed / sharedRubric.criteria.length) * 100);
  }, [sharedRubric, gradingData.rubricGrading]);

  const classCompletionPercentage = useMemo(() => {
    const students =
      gradingSession?.classListData?.students ||
      classList?.students ||
      (Array.isArray(classList) ? classList : []) ||
      [];

    if (!Array.isArray(students) || students.length === 0) return 0;

    const gradingProgress =
      gradingSession?.classListData?.gradingProgress ||
      classList?.gradingProgress ||
      [];

    const finalizedCount = students.reduce((count, student, index) => {
      if (!student?.id) return count;

      const status = getGradeStatus(student.id);
      if (status === 'final') return count + 1;

      const progressEntry = gradingProgress[index];
      const isFinalized =
        progressEntry?.gradeType === 'final' ||
        progressEntry?.status === 'completed_final';

      return isFinalized ? count + 1 : count;
    }, 0);

    return Math.round((finalizedCount / students.length) * 100);
  }, [classList, gradingSession, getGradeStatus]);

  const fileInputRef = useRef(null);
  const rubricInputRef = useRef(null);

  // Video link management
  const [videoLinkInput, setVideoLinkInput] = useState('');
  const [videoTitle, setVideoTitle] = useState('');

  useEffect(() => {
    if (!gradingResetToken) return;

    setExpandedCriteria({});
    setShowRubricComments({});
    setAIFeedbackPrompt('');
    setVideoLinkInput('');
    setVideoTitle('');
  }, [gradingResetToken]);

  // PRIVACY: Session monitoring effect
  useEffect(() => {
    if (!sessionExpirationEnabled) {
      setSessionWarning(false);
      setTimeRemaining(null);
      return;
    }

    if (!sessionActive) {
      setSessionWarning(true);
      return;
    }

    if (sessionManager) {
      // Update time remaining every minute
      const interval = setInterval(() => {
        const remaining = sessionManager.getTimeRemaining();
        setTimeRemaining(remaining);

        // Show warning at 5 minutes
        if (remaining <= 5 * 60 * 1000 && remaining > 0) {
          setSessionWarning(true);
        }
      }, 60000);

      // Initial check
      const remaining = sessionManager.getTimeRemaining();
      setTimeRemaining(remaining);
      if (remaining <= 5 * 60 * 1000 && remaining > 0) {
        setSessionWarning(true);
      }

      return () => clearInterval(interval);
    }
  }, [sessionActive, sessionManager, sessionExpirationEnabled]);

  // Safely access the active late policy levels
  const customLatePolicies = Object.values(courseLatePolicies || {});

  const getPolicyById = (policyId) => {
    if (!policyId) return null;
    if (policyId === DEFAULT_LATE_POLICY.id) return DEFAULT_LATE_POLICY;
    return customLatePolicies.find(p => p.id === policyId) || null;
  };

  const getSafeLatePolicy = (policyId) => getPolicyById(policyId) || currentLatePolicy || DEFAULT_LATE_POLICY;

  // Enhanced shared data synchronization
  useEffect(() => {
    if (!sharedGradingData) return;

    console.log('🔄 Checking shared grading data for course info:', sharedGradingData.course);

    // Only update course info if it's not empty and different from current
    if (sharedGradingData.course &&
      (sharedGradingData.course.code || sharedGradingData.course.name ||
        sharedGradingData.course.instructor || sharedGradingData.course.term)) {

      const needsSync =
        sharedGradingData.course.code !== localGradingData.course.code ||
        sharedGradingData.course.name !== localGradingData.course.name ||
        sharedGradingData.course.instructor !== localGradingData.course.instructor ||
        sharedGradingData.course.evaluator !== localGradingData.course.evaluator ||
        sharedGradingData.course.term !== localGradingData.course.term;

      if (needsSync) {
        console.log('✅ Syncing course data from shared context to local state');
        setLocalGradingData(prev => ({
          ...prev,
          course: {
            ...prev.course,
            code: sharedGradingData.course.code || prev.course.code,
            name: sharedGradingData.course.name || prev.course.name,
            instructor: sharedGradingData.course.instructor || prev.course.instructor,
            evaluator: sharedGradingData.course.evaluator || prev.course.evaluator,
            term: sharedGradingData.course.term || prev.course.term
          }
        }));
      }
    }
  }, [sharedGradingData, localGradingData.course.code, localGradingData.course.name, localGradingData.course.instructor, localGradingData.course.evaluator, localGradingData.course.term]);

  // Auto-populate assignment info when rubric is loaded
  useEffect(() => {
    if (sharedRubric) {
      const initialGrading = {};
      sharedRubric.criteria.forEach(criterion => {
        initialGrading[criterion.id] = {
          criterionId: criterion.id,
          selectedLevel: null,
          customComments: ''
        };
      });

      setLocalGradingData(prevData => ({
        ...prevData,
        assignment: {
          ...prevData.assignment,
          name: sharedRubric.assignmentInfo?.title || prevData.assignment.name,
          maxPoints: sharedRubric.assignmentInfo?.totalPoints || prevData.assignment.maxPoints
        },
        rubricGrading: prevData.rubricGrading && Object.keys(prevData.rubricGrading).length > 0 ? prevData.rubricGrading : initialGrading,
        metadata: {
          ...prevData.metadata,
          rubricIntegrated: true
        }
      }));
    }
  }, [sharedRubric]);

  // Auto-populate assignment date from assignment prompt page when available
  useEffect(() => {
    if (assignmentPromptFormData?.dueDateCustom) {
      setLocalGradingData(prevData => {
        // Only auto-populate if no date is already set
        if (!prevData.assignment.dueAt && !prevData.assignment.dueDate) {
          const dateVal = assignmentPromptFormData.dueDateCustom;
          return {
            ...prevData,
            assignment: {
              ...prevData.assignment,
              dueDate: dateVal,
              dueAt: dateVal.includes('T') ? dateVal : `${dateVal}T23:59`
            }
          };
        }
        return prevData;
      });
    }
  }, [assignmentPromptFormData?.dueDateCustom]);

  useEffect(() => {
    const resetToken = sharedGradingData?.metadata?.rubricResetToken;
    if (!resetToken || rubricResetTokenRef.current === resetToken) return;

    rubricResetTokenRef.current = resetToken;

    const baseRubricGrading = {};
    if (sharedRubric?.criteria?.length) {
      sharedRubric.criteria.forEach((criterion) => {
        baseRubricGrading[criterion.id] = {
          criterionId: criterion.id,
          selectedLevel: null,
          customComments: ''
        };
      });
    }

    const fallbackCourse = sharedCourseDetails?.course || { code: '', name: '', instructor: '', term: '' };
    const fallbackAssignment = sharedCourseDetails?.assignment || { name: '', title: '', dueDate: '', dueAt: '', maxPoints: 100, totalPoints: 100 };

    setLocalGradingData({
      student: currentStudent || { name: '', id: '', email: '' },
      course: sharedGradingData?.course || fallbackCourse,
      assignment: {
        ...fallbackAssignment,
        ...(sharedGradingData?.assignment || {}),
        name: sharedGradingData?.assignment?.name || sharedGradingData?.assignment?.title || fallbackAssignment.name || '',
        title: sharedGradingData?.assignment?.title || sharedGradingData?.assignment?.name || fallbackAssignment.title || '',
        maxPoints: sharedRubric?.assignmentInfo?.totalPoints || sharedGradingData?.assignment?.maxPoints || fallbackAssignment.maxPoints || 100,
        totalPoints: sharedRubric?.assignmentInfo?.totalPoints || sharedGradingData?.assignment?.totalPoints || fallbackAssignment.totalPoints || fallbackAssignment.maxPoints || 100
      },
      feedback: { general: '', strengths: '', improvements: '' },
      attachments: [],
      videoLinks: [],
      latePolicy: {
        penaltyApplied: false,
        penaltyPercentage: 0,
        daysLate: 0,
        policyId: DEFAULT_LATE_POLICY.id,
        originalScore: null,
        adjustedScore: null,
        ...(sharedGradingData?.latePolicy || {})
      },
      metadata: {
        gradedBy: '',
        gradedDate: '',
        aiAssisted: false,
        rubricIntegrated: true,
        submittedAt: '',
        hasApprovedExtension: false,
        lateOverrideDays: null,
        ...(sharedGradingData?.metadata || {})
      },
      rubricGrading: baseRubricGrading
    });
  }, [sharedGradingData, sharedRubric, sharedCourseDetails, currentStudent]);

  // PRIVACY: Auto-save to shared context whenever gradingData changes (with debounce) - SESSION ONLY
  useEffect(() => {
    if (!sessionActive) return; // Don't save if session is expired

    const timeoutId = setTimeout(() => {
      setSharedGradingData(gradingData);
      setSharedCourseDetails({
        student: gradingData.student,
        course: gradingData.course,
        assignment: gradingData.assignment
      });
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [gradingData, setSharedGradingData, setSharedCourseDetails, sessionActive]);

  useEffect(() => {
    if (!sessionActive || !currentStudent?.id) return undefined;

    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }

    autosaveTimeoutRef.current = setTimeout(() => {
      persistDraftForCurrentStudent(localGradingData);
      autosaveTimeoutRef.current = null;
    }, 400);

    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
        autosaveTimeoutRef.current = null;
      }
    };
  }, [currentStudent?.id, localGradingData, persistDraftForCurrentStudent, sessionActive]);

  // When student changes, load any saved draft/final but keep course+assignment
  useEffect(() => {
    if (!currentStudent?.id) return;

    const status = getGradeStatus(currentStudent.id);
    let saved = null;
    if (status === 'final' && loadFinalGrade) {
      saved = loadFinalGrade(currentStudent.id);
    }

    if (!saved && loadDraft) {
      saved = loadDraft(currentStudent.id);
    }

    if (saved) {
      setLocalGradingData(prev => ({
        ...saved, // Use saved data
        course: prev.course, // Keep current course info
        assignment: prev.assignment // Keep current assignment info
      }));
    } else {
      // Fresh slate for this student, but keep course/assignment
      setLocalGradingData(prev => ({
        ...prev,
        student: currentStudent,
        feedback: { general: '', strengths: '', improvements: '' },
        attachments: [],
        videoLinks: [],
        latePolicy: { penaltyApplied: false, penaltyPercentage: 0, daysLate: 0, policyId: currentLatePolicy?.id },
        rubricGrading: {}
      }));
    }

    setAIFeedbackPrompt('');
  }, [currentStudent?.id, gradingResetToken, currentLatePolicy?.id]);

  useEffect(() => () => {
    if (rubricPersistTimeoutRef.current) {
      clearTimeout(rubricPersistTimeoutRef.current);
      rubricPersistTimeoutRef.current = null;
    }

    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
      autosaveTimeoutRef.current = null;
    }
  }, []);

  // Note: unmount draft save is handled by the ref-based cleanup effect above
  // (latestGradingDataRef / latestStudentRef / saveDraftRef), which reliably
  // captures the latest values via refs instead of depending on closure timing.

  const getGroupLevels = useCallback(
    (groupId) => {
      const group = sharedRubric?.groups?.find((g) => g.id === groupId);
      if (group?.levels?.length) return group.levels;
      return sharedRubric?.rubricLevels || [];
    },
    [sharedRubric?.groups, sharedRubric?.rubricLevels]
  );

  const getLevelsForCriterion = useCallback((criterion) => getGroupLevels(criterion?.groupId), [getGroupLevels]);

  const findLevelForCriterion = useCallback(
    (criterion, levelKey) => getLevelsForCriterion(criterion).find((level) => level.level === levelKey),
    [getLevelsForCriterion]
  );

  // Enhanced calculation function
  const calculateTotalScore = () => {
    if (!sharedRubric || !sharedRubric.criteria) return { rawScore: 0, finalScore: 0, finalPercentage: 0, penaltyApplied: false, latePolicyDescription: '' };

    const rubricTotalPoints = sharedRubric?.assignmentInfo?.totalPoints || gradingData.assignment?.maxPoints || 100;

    const rubricScore = calculateRubricFinalScore({
      criteria: sharedRubric.criteria,
      rubricLevels: sharedRubric.rubricLevels,
      rubricGrading: gradingData.rubricGrading,
      rubricTotalPoints,
      getLevelsForCriterion
    });

    // Normalize late override to a safe number (or undefined) so policy math always fires
    const rawOverride = gradingData.metadata?.lateOverrideDays;
    const normalizedLateOverride = rawOverride === null || rawOverride === undefined
      ? undefined
      : Number(rawOverride);

    const { finalScore, penaltyPercentage, daysLate, appliedPolicy } = applyLatePolicyToScore({
      rawScore: rubricScore.finalPoints,
      dueAt: gradingData.assignment?.dueAt || gradingData.assignment?.dueDate,
      submittedAt: gradingData.metadata?.submittedAt,
      hasApprovedExtension: gradingData.metadata?.hasApprovedExtension,
      courseId: gradingData.course?.code,
      daysLateOverride: Number.isFinite(normalizedLateOverride) ? normalizedLateOverride : undefined
    });

    return {
      rawScore: rubricScore.finalPoints,
      finalScore,
      finalPercentage: rubricScore.finalPercentage,
      penaltyApplied: penaltyPercentage > 0,
      penaltyPercentage,
      daysLate,
      latePolicyDescription: appliedPolicy?.description || '',
      appliedPolicy
    };
  };

  const scoreSummary = useMemo(
    () => calculateTotalScore(),
    [gradingData, sharedRubric, applyLatePolicyToScore]
  );

  useEffect(() => {
    // Keep late-policy details in grading data so exports and reports stay in sync
    setGradingData(prev => {
      const nextLatePolicy = {
        penaltyApplied: scoreSummary.penaltyPercentage > 0,
        penaltyPercentage: scoreSummary.penaltyPercentage,
        daysLate: scoreSummary.daysLate,
        policyId: scoreSummary.appliedPolicy?.id || prev.latePolicy?.policyId,
        originalScore: scoreSummary.rawScore,
        adjustedScore: scoreSummary.finalScore
      };

      const unchanged = prev.latePolicy
        && prev.latePolicy.penaltyApplied === nextLatePolicy.penaltyApplied
        && prev.latePolicy.penaltyPercentage === nextLatePolicy.penaltyPercentage
        && prev.latePolicy.daysLate === nextLatePolicy.daysLate
        && prev.latePolicy.policyId === nextLatePolicy.policyId
        && prev.latePolicy.originalScore === nextLatePolicy.originalScore
        && prev.latePolicy.adjustedScore === nextLatePolicy.adjustedScore;

      if (unchanged) return prev;

      return {
        ...prev,
        latePolicy: nextLatePolicy
      };
    });
  }, [scoreSummary, setGradingData]);

  const effectiveLatePolicy = getEffectiveLatePolicy(gradingData.course?.code);
  const penaltyEntries = Object.entries(effectiveLatePolicy.penaltiesByDay || {})
    .filter(([, penalty]) => penalty > 0)
    .sort((a, b) => Number(a[0]) - Number(b[0]));
  const latePolicySummary = describeLatePolicy(effectiveLatePolicy);

  const submissionTimingOptions = useMemo(() => {
    const maxDay = effectiveLatePolicy.maxLateDays || (penaltyEntries.length ? Number(penaltyEntries[penaltyEntries.length - 1][0]) : DEFAULT_LATE_POLICY.maxLateDays);
    const baseOptions = [
      { label: 'On time (use timestamps)', value: null }
    ];

    const policyOptions = penaltyEntries.map(([day, penalty], index) => {
      const numericDay = Number(day);
      const isTerminal = numericDay >= maxDay || index === penaltyEntries.length - 1;
      const hoursLate = numericDay * 24;
      const penaltyPercent = Math.round(penalty * 100);
      const dayLabel = `${hoursLate}${isTerminal ? '+' : ''} hours late (${numericDay}${isTerminal ? '+' : ''} day${numericDay === 1 ? '' : 's'})`;
      return {
        label: `${dayLabel} — ${penaltyPercent}% penalty`,
        value: numericDay,
      };
    });

    return [...baseOptions, ...policyOptions];
  }, [effectiveLatePolicy.maxLateDays, penaltyEntries]);

  const rubricTotalPoints = sharedRubric?.assignmentInfo?.totalPoints || gradingData.assignment?.maxPoints || 100;
  const roundedSummaryScore = Math.round(scoreSummary.finalScore);
  const roundedSummaryPercentage = rubricTotalPoints > 0
    ? Math.round((scoreSummary.finalScore / rubricTotalPoints) * 100)
    : 0;
  const openInlineEditor = (content, field, onSave) => {
    richTextContentRef.current = content || '';
    setInlineEditor({ show: true, content: content || '', field, onSave });
  };

  const closeInlineEditor = (save = false) => {
    if (save && inlineEditor.onSave) {
      inlineEditor.onSave(richTextContentRef.current);
    }
    setInlineEditor({ show: false, content: '', field: null, onSave: null });
  };

  const updateRubricGrading = (criterionId, level, comments = null, customPoints = null) => {
    if (!sharedRubric) return; // Guard against null rubric

    setLocalGradingData(prevData => {
      const existingGrading = prevData.rubricGrading[criterionId];
      const existingComments = existingGrading?.customComments || '';

      // When changing level, reset custom points to null (will use default base value)
      const shouldResetPoints = existingGrading?.selectedLevel !== level;

      const newRubricState = {
        ...prevData.rubricGrading,
        [criterionId]: {
          criterionId,
          selectedLevel: level,
          customComments: comments !== null ? comments : existingComments,
          customPoints: shouldResetPoints ? null : (customPoints !== undefined ? customPoints : existingGrading?.customPoints)
        }
      };

      const nextData = {
        ...prevData,
        rubricGrading: newRubricState
      };

      return nextData;
    });
  };

  const updateCustomPoints = (criterionId, customPoints) => {
    if (!sharedRubric) return;

    setLocalGradingData(prevData => {
      const currentGrading = prevData.rubricGrading[criterionId];
      if (!currentGrading?.selectedLevel) return prevData;

      // Get the criterion and valid range
      const criterion = sharedRubric.criteria.find(c => c.id === criterionId);
      if (!criterion) return prevData;

      const levelsForCriterion = getLevelsForCriterion(criterion);
      const { min, max } = getValidPointRange({
        criterion,
        levelKey: currentGrading.selectedLevel,
        rubricLevels: levelsForCriterion
      });

      // Clamp the value to the valid range
      const numericValue = Number(customPoints);
      const clampedValue = Math.max(min, Math.min(max, numericValue));

      return {
        ...prevData,
        rubricGrading: {
          ...prevData.rubricGrading,
          [criterionId]: {
            ...currentGrading,
            customPoints: Number.isFinite(clampedValue) ? clampedValue : null
          }
        }
      };
    });
  };

  const addCriterionFeedback = (criterionId, comment) => {
    if (!sharedRubric) return; // Guard against null rubric

    setLocalGradingData(prevData => {
      const currentGrading = prevData.rubricGrading[criterionId];
      const currentComments = currentGrading?.customComments || '';
      const newComments = currentComments ?
        `${currentComments}\n${comment}` : comment;

      return {
        ...prevData,
        rubricGrading: {
          ...prevData.rubricGrading,
          [criterionId]: {
            ...currentGrading,
            customComments: newComments
          }
        }
      };
    });
  };

  // HELPER FUNCTIONS FOR COURSE INFO
  const getEffectiveCourseData = () => {
    const course = gradingData?.course || {};
    const metadata = classList?.courseMetadata;

    return {
      code: course.code || metadata?.courseCode || '',
      name: course.name || metadata?.courseName || '',
      instructor: course.instructor || metadata?.instructor || metadata?.professors || '',
      evaluator: course.evaluator || '',
      term: course.term || metadata?.term || ''
    };
  };

  const pullCourseDataFromClassList = () => {
    if (!classList?.courseMetadata) {
      alert('No class list course metadata available to pull from.');
      return;
    }

    const metadata = classList.courseMetadata;
    setGradingData(prevData => ({
      ...prevData,
      course: {
        ...prevData.course,
        code: metadata.courseCode || prevData.course.code,
        name: metadata.courseName || prevData.course.name,
        instructor: metadata.instructor || metadata.professors || prevData.course.instructor,
        term: metadata.term || prevData.course.term
      }
    }));

    // Auto-populate assignment date from assignment prompt data if available
    if (assignmentPromptFormData?.dueDateCustom) {
      setGradingData(prevData => {
        if (!prevData.assignment.dueAt && !prevData.assignment.dueDate) {
          const dateVal = assignmentPromptFormData.dueDateCustom;
          return {
            ...prevData,
            assignment: {
              ...prevData.assignment,
              dueDate: dateVal,
              dueAt: dateVal.includes('T') ? dateVal : `${dateVal}T23:59`
            }
          };
        }
        return prevData;
      });
    }

    alert('Course information has been pulled from the class list!');
  };

  // LETTER GRADE CALCULATION – delegates to single-source-of-truth computeGrade()
  const getLetterGrade = (percentage) => {
    const programType = classList?.courseMetadata?.programType || 'Degree';
    return computeGrade({ programType, percent: percentage }).letter;
  };

  // Enhanced version of your existing generateStudentReportHTML function (from GradingTemplate.js)
  const generateStudentReportHTML = () => {
    // Calculate scores
    const scoreCalculation = calculateTotalScore();
    const totalScore = scoreCalculation.finalScore;
    const rawScore = scoreCalculation.rawScore;
    const maxPoints = sharedRubric ? sharedRubric.assignmentInfo.totalPoints : gradingData.assignment.maxPoints;
    const roundedScore = Math.round(totalScore);
    const roundedPercentage = maxPoints > 0 ? Math.round((totalScore / maxPoints) * 100) : 0;
    const penaltyApplied = scoreCalculation.penaltyApplied;

    // Generate attachments HTML
    const attachmentsHTML = gradingData.attachments?.map((att, index) => {
      if (att.base64Data) {
        return `<div class="attachment-item"><img src="${att.base64Data}" alt="${att.name}" class="clickable-image" data-index="${index}" style="max-width: 200px; max-height: 200px; object-fit: contain; display: block; margin-bottom: 0.5rem; border: 1px solid #ddd; border-radius: 4px;" /><div style="font-size: 0.875rem; font-weight: 500; word-break: break-word;">${att.name}</div><div style="font-size: 0.75rem; color: #666;">${(att.size / 1024).toFixed(1)} KB</div><div style="font-size: 0.75rem; color: #007bff; margin-top: 4px;">Click to enlarge</div></div>`;
      } else {
        return `<div class="attachment-item"><div style="width: 40px; height: 40px; background: #f3f4f6; border-radius: 4px; display: flex; align-items: center; justify-content: center; margin: 0 auto 0.5rem auto;">📄</div><div style="font-size: 0.875rem; font-weight: 500; word-break: break-word;">${att.name}</div><div style="font-size: 0.75rem; color: #666;">${(att.size / 1024).toFixed(1)} KB</div></div>`;
      }
    }).join('') || '';

    // Generate video links HTML
    const videoLinksHTML = gradingData.videoLinks?.map(link =>
      `<div class="video-link-item" style="background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 8px; padding: 1rem; margin-bottom: 1rem;"><div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem;"><span style="font-size: 1.25rem;">🎥</span><strong style="color: #495057;">${link.title}</strong></div><a href="${link.url}" target="_blank" rel="noopener noreferrer" style="color: #007bff; text-decoration: none; word-break: break-all;">${link.url}</a></div>`
    ).join('') || '';

    // Generate rubric table HTML
    const rubricTableHTML = sharedRubric ? `
        <div style="margin: 30px 0;">
            <h3>📊 Detailed Rubric Assessment</h3>
            <table style="width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 0.9rem;">
                <thead>
                    <tr style="background: #f8f9fa;">
                        <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Criterion</th>
                        <th style="border: 1px solid #ddd; padding: 12px; text-align: center; width: 120px;">Level Achieved</th>
                        <th style="border: 1px solid #ddd; padding: 12px; text-align: center; width: 80px;">Points</th>
                        <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">Details & Comments</th>
                    </tr>
                </thead>
                <tbody>
                    ${sharedRubric.criteria.map(criterion => {
      const rubricGrading = gradingData.rubricGrading?.[criterion.id];
      const selectedLevel = rubricGrading?.selectedLevel;
      const levelSet = getLevelsForCriterion(criterion);
      const level = selectedLevel ? levelSet.find(l => l.level === selectedLevel) : null;
      const levelDef = selectedLevel ? criterion.levels?.[selectedLevel] : null;
      const customPoints = rubricGrading?.customPoints;
      const points = calculateLevelPoints({
        criterion,
        levelKey: selectedLevel,
        rubricLevels: levelSet,
        customPoints
      });
      const levelDescription = levelDef?.description || '';
      const additionalComments = rubricGrading?.customComments || '';
      const { background: levelColor, text: levelTextColor } = getLevelBadgeColors(level?.color);

      return `<tr>
                            <td style="border: 1px solid #ddd; padding: 12px; vertical-align: top;">
                                <strong>${criterion.name}</strong><br>
                                <small style="color: #666;">${criterion.description || ''}</small><br>
                                <small style="color: #888;">Max Points: ${criterion.maxPoints}</small>
                            </td>
                            <td style="border: 1px solid #ddd; padding: 8px; text-align: center; vertical-align: top;">
                                <span style="display: inline-block; padding: 4px 8px; border-radius: 4px; font-size: 0.8rem; font-weight: bold; background: ${levelColor}; color: ${levelTextColor}; border: 1px solid ${levelColor};">
                                    ${level?.name || 'Not Assessed'}
                                </span>
                            </td>
                            <td style="border: 1px solid #ddd; padding: 8px; text-align: center; font-weight: bold; font-size: 1.1em; color: #2c3e50;">${points}</td>
                            <td style="border: 1px solid #ddd; padding: 8px; vertical-align: top;">
                                ${levelDescription ? `<div style="margin-bottom: 10px; padding: 8px; background: #f8f9fa; border-left: 4px solid ${levelColor}; border-radius: 4px;"><strong style="color: ${levelColor};">Level Description:</strong><br><span style="font-size: 0.85em; line-height: 1.4;">${levelDescription}</span></div>` : ''}
                                ${additionalComments ? `<div style="margin-top: 8px; padding: 8px; background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 4px;"><strong style="color: #856404;">Additional Comments:</strong><br><span style="font-size: 0.85em; line-height: 1.4; white-space: pre-wrap;">${additionalComments}</span></div>` : ''}
                                ${(!levelDescription && !additionalComments) ? '<em style="color: #999;">No assessment provided</em>' : ''}
                            </td>
                        </tr>`;
    }).join('')}
                </tbody>
            </table>
            <div style="background: #e8f5e8; border: 1px solid #4caf50; border-radius: 8px; padding: 15px; margin-top: 20px;">
                <h4 style="color: #2e7d32; margin-bottom: 10px;">📊 Rubric Score Summary</h4>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px;">
                    <div><strong>Total Score:</strong> ${roundedScore} / ${maxPoints}<br><strong>Percentage:</strong> ${roundedPercentage}%</div>
                    <div><strong>Grade Status:</strong><span style="color: ${roundedPercentage >= (sharedRubric.assignmentInfo.passingThreshold || 60) ? '#4caf50' : '#f44336'}; font-weight: bold;">${roundedPercentage >= (sharedRubric.assignmentInfo.passingThreshold || 60) ? '✓ PASSING' : '✗ NEEDS IMPROVEMENT'}</span></div>
                    ${penaltyApplied ? `<div style="color: #ff9800;"><strong>Late Penalty Applied:</strong><br>Raw Score: ${Math.round(rawScore * 10) / 10}</div>` : ''}
                </div>
            </div>
        </div>` : '';

    // Use the logo utility to generate header
    const reportData = {
      title: `📋 Individual Grade Report - ${gradingData.student.name}`,
      courseCode: gradingData.course.code || '',
      courseName: gradingData.course.name || '',
      section: gradingData.course.section || '',
      rubricTitle: sharedRubric?.assignmentInfo?.title || 'Assessment'
    };

    // Complete HTML document with logo integration
    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Grade Report - ${gradingData.student.name}</title>
    ${getReportStyles()}
    <style>
        .score-summary { background: #e8f5e8; border: 2px solid #4caf50; border-radius: 8px; padding: 20px; margin: 20px 0; text-align: center; }
        .late-policy-section { margin: 30px 0; background: #fff5f5; border: 1px solid #f87171; border-radius: 8px; padding: 20px; }
        .feedback-section { margin: 20px 0; padding: 15px; background: #f9f9f9; border-radius: 8px; }
        .attachments { margin: 30px 0; }
        .attachment-item { display: inline-block; text-align: center; margin: 1rem; padding: 1rem; background: #fff; border: 1px solid #ddd; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,.1); max-width: 250px; vertical-align: top; }
        .image-lightbox { display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.75); align-items: center; justify-content: center; z-index: 9999; padding: 1.5rem; }
        .image-lightbox img { max-width: 90vw; max-height: 90vh; border-radius: 8px; box-shadow: 0 12px 30px rgba(0,0,0,0.35); }
        .image-lightbox-close { position: absolute; top: 24px; right: 24px; background: #fff; color: #111; border: none; border-radius: 9999px; width: 36px; height: 36px; font-size: 1.25rem; cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,0.2); }
    </style>
</head>
<body>
    ${generateReportHeader(reportData)}

    <div class="score-summary">
        <h2>📊 Final Score</h2>
        <div style="font-size: 2rem; font-weight: 700; color: #2e7d32; margin: 15px 0;">
            ${roundedScore} / ${maxPoints} (${roundedPercentage}%)
        </div>
        <p style="margin: 10px 0; color: #555;">
            ${sharedRubric ? `Rubric: ${sharedRubric.assignmentInfo.title}` : ""}
            ${penaltyApplied ? ` | Late Policy Applied` : ""}
        </p>
    </div>

    ${penaltyApplied ? `
        <div class="late-policy-section">
            <h3 style="color: #dc2626;">📅 Late Submission Policy Applied</h3>
            <p><strong>Raw Score:</strong> ${Math.round(rawScore * 10) / 10}/${maxPoints} → <strong>Final Score:</strong> ${roundedScore}/${maxPoints}</p>
        </div>
    ` : ""}

    ${rubricTableHTML}

    ${Object.entries(gradingData.feedback || {}).filter(([key, text]) => text).map(([key, text]) => `
        <div class="feedback-section">
            <h3>${key.charAt(0).toUpperCase() + key.slice(1)} Feedback</h3>
            <p>${text.replace(/\n/g, "<br>")}</p>
        </div>
    `).join("")}

    ${attachmentsHTML ? `
        <div class="attachments">
            <h3>📎 File Attachments</h3>
            <div style="display: flex; flex-wrap: wrap; justify-content: flex-start;">
                ${attachmentsHTML}
            </div>
        </div>
    ` : ""}

    ${videoLinksHTML ? `
        <div class="video-links">
            <h3>🎥 Video Review Links</h3>
            ${videoLinksHTML}
        </div>
    ` : ""}

    <div id="image-lightbox" class="image-lightbox">
        <div style="position: relative; max-width: 90vw; max-height: 90vh;">
            <img id="lightbox-image" src="" alt="Attachment preview" />
            <button id="lightbox-close" class="image-lightbox-close">×</button>
        </div>
    </div>

    <script>
      (function() {
        const overlay = document.getElementById('image-lightbox');
        const previewImg = document.getElementById('lightbox-image');
        const closeBtn = document.getElementById('lightbox-close');

        function openOverlay(src, alt) {
          if (!src) return;
          previewImg.src = src;
          previewImg.alt = alt || 'Attachment preview';
          overlay.style.display = 'flex';
        }

        function closeOverlay() {
          overlay.style.display = 'none';
          previewImg.src = '';
        }

        document.querySelectorAll('.clickable-image').forEach(img => {
          img.addEventListener('click', () => openOverlay(img.src, img.alt));
        });

        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
            closeOverlay();
          }
        });

        closeBtn.addEventListener('click', closeOverlay);
        document.addEventListener('keyup', (e) => {
          if (e.key === 'Escape') {
            closeOverlay();
          }
        });
      })();
    </script>

    <div class="footer">
        <p>Course Professor: ${gradingData.course.instructor || 'Course Instructor'}</p>
        ${gradingData.course.evaluator ? `<p>Course Evaluator: ${gradingData.course.evaluator}</p>` : ''}
    </div>
</body>
</html>`;
  };

  // AI FEEDBACK EXPORT FUNCTIONS
  const parseStudentName = (name = '') => {
    if (!name) {
      return { firstName: '', lastName: '', fullName: '' };
    }

    const [lastPart = '', firstPart = ''] = name.split(',').map(part => part.trim());
    const normalizedFullName = name.trim();

    const firstName = firstPart || normalizedFullName.split(/\s+/).slice(-1)[0] || '';
    const lastName = lastPart || (normalizedFullName.split(/\s+/)[0] || '');

    return { firstName, lastName, fullName: normalizedFullName };
  };

  const buildAIFeedbackData = () => {
    if (!localGradingData.student?.name) {
      alert('Please ensure student information is loaded before exporting for AI feedback.');
      return;
    }

    // Calculate scores
    const maxPoints = sharedRubric?.assignmentInfo?.totalPoints ?? gradingData.assignment?.maxPoints ?? 0;
    const roundedPercentage = maxPoints > 0 ? Math.round((scoreSummary.finalScore / maxPoints) * 100) : 0;
    const passingThreshold = sharedRubric?.assignmentInfo?.passingThreshold ?? 0;

    const safeNumber = (value, fallback = 0) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    // Build criteria performance data
    const criteriaPerformance = (sharedRubric && sharedRubric.criteria)
      ? sharedRubric.criteria.map(criterion => {
        const grading = gradingData.rubricGrading[criterion.id];
        const levelSet = getLevelsForCriterion(criterion);
        const selectedLevel = grading?.selectedLevel
          ? levelSet.find(level => level.level === grading.selectedLevel)
          : null;
        const levelDef = grading?.selectedLevel ? criterion.levels?.[grading.selectedLevel] : null;

        const maxPointsValue = safeNumber(criterion.maxPoints);
        const earnedPoints = levelDef
          ? calculateLevelPoints({
              criterion,
              levelKey: grading.selectedLevel,
              rubricLevels: levelSet
            }).toFixed(1)
          : '';

        return {
          name: criterion.name,
          maxPoints: maxPointsValue,
          earnedPoints,
          level: selectedLevel?.name || '',
          levelDescription: selectedLevel?.description || '',
          customComments: grading?.customComments?.trim() || '',
          percentage: levelDef && maxPointsValue > 0
            ? Math.round((calculateLevelPoints({
                criterion,
                levelKey: grading.selectedLevel,
                rubricLevels: levelSet
              }) / maxPointsValue) * 100)
            : 0
        };
      })
      : [];

    const { firstName, lastName, fullName } = parseStudentName(localGradingData.student.name);

    const aiGradeData = {
      student: {
        firstName,
        lastName,
        fullName,
        id: localGradingData.student.id,
        email: localGradingData.student.email
      },
      course: {
        code: localGradingData.course.code,
        name: localGradingData.course.name,
        instructor: localGradingData.course.instructor,
        evaluator: localGradingData.course.evaluator,
        term: localGradingData.course.term
      },
      assignment: {
        title: localGradingData.assignment.name || (sharedRubric?.assignmentInfo?.title),
        description: sharedRubric?.assignmentInfo?.description || '',
        totalPoints: safeNumber(maxPoints),
        passingThreshold: safeNumber(passingThreshold),
        dueDate: localGradingData.assignment.dueDate || ''
      },
      gradeData: {
        overallScore: safeNumber(Math.round(scoreSummary.finalScore)),
        rawScore: safeNumber(Math.round(scoreSummary.rawScore * 10) / 10),
        overallPercentage: safeNumber(roundedPercentage),
        letterGrade: getLetterGrade(roundedPercentage) || '',
        passed: roundedPercentage >= passingThreshold,
        latePenaltyApplied: Boolean(scoreSummary.penaltyApplied),
        latePolicyDescription: scoreSummary.latePolicyDescription || '',
        criteria: criteriaPerformance
      },
      existingFeedback: {
        general: (localGradingData.feedback.general || '').trim(),
        strengths: (localGradingData.feedback.strengths || '').trim(),
        improvements: (localGradingData.feedback.improvements || '').trim()
      },
      metadata: {
        gradedDate: new Date().toISOString(),
        rubricUsed: sharedRubric?.assignmentInfo?.title || '',
        totalCriteria: criteriaPerformance.length,
        exportedForAI: true
      }
    };

    return aiGradeData;
  };

  const generateAIFeedbackPrompt = (gradeDataParam) => {
    const gradeData = gradeDataParam || buildAIFeedbackData();
    if (!gradeData) return;

    const prompt = `NEW STUDENT — START FRESH COMMENTS
You are an experienced instructor writing concise, personal feedback for a student. Write feedback that sounds natural and conversational, but keep it brief and focused. Always address the student by their first name only.

STUDENT GRADE DATA:
${JSON.stringify(gradeData, null, 2)}

Using the JSON above — especially the "existingFeedback" fields — generate feedback for the student in up to three sections that match the grading tool:

- "general"      → Overall comments about the assignment
- "strengths"    → What the student did well
- "improvements" → Suggestions for how the student can improve

IMPORTANT RULE:
If a section has no instructor comments (empty or whitespace), omit that section entirely. Do NOT mention missing sections in the output.

OUTPUT FORMAT (Markdown headings):

If general has content:

**Overall Comments**
Write 2–4 supportive, honest, concise sentences.

If strengths has content:

**Strengths**
Write 2–4 sentences based on the strengths comments and relevant rubric notes.

If improvements has content:

**Areas for Improvement**
Write 2–4 sentences with clear, actionable suggestions.

WRITING STYLE:
- Speak directly to the student using their first name only.
- Keep the tone warm, professional, and accessible for a first-year college student.
- Do not reference JSON, field names, or internal processes.
- Base all feedback solely on the information provided through the grading tool.`.trim();

    console.log('AI Feedback Prompt Generated:', prompt);
    return prompt;
  };

  const exportForAIFeedback = () => {
    try {
      const gradeData = buildAIFeedbackData();
      if (!gradeData) return;

      const prompt = generateAIFeedbackPrompt(gradeData);
      if (!prompt) return;

      setAIFeedbackPrompt(prompt);

    } catch (error) {
      console.error('Error exporting AI feedback data:', error);
      alert('Error exporting AI feedback data. Please check the console for details.');
    }
  };

  // FILE UPLOAD HANDLERS
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const processFiles = files.map(file => {
      return new Promise((resolve) => {
        const newAttachment = {
          id: Date.now() + Math.random(),
          name: file.name,
          size: file.size,
          type: file.type,
          file: file
        };

        // If it's an image, convert to base64 for display
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e) => {
            newAttachment.base64Data = e.target.result;
            resolve(newAttachment);
          };
          reader.readAsDataURL(file);
        } else {
          resolve(newAttachment);
        }
      });
    });

    Promise.all(processFiles).then(newAttachments => {
      setGradingData(prevData => ({
        ...prevData,
        attachments: [...prevData.attachments, ...newAttachments]
      }));
    });
  };

  const removeAttachment = (attachmentId) => {
    setGradingData(prevData => ({
      ...prevData,
      attachments: prevData.attachments.filter(att => att.id !== attachmentId)
    }));
  };

  // VIDEO LINK HANDLERS
  const addVideoLink = () => {
    if (!videoLinkInput.trim()) return;

    const newVideoLink = {
      id: Date.now(),
      url: videoLinkInput,
      title: videoTitle || 'Video Link',
      addedDate: new Date().toLocaleDateString()
    };

    setGradingData(prevData => ({
      ...prevData,
      videoLinks: [...prevData.videoLinks, newVideoLink]
    }));

    setVideoLinkInput('');
    setVideoTitle('');
  };

  const removeVideoLink = (linkId) => {
    setGradingData(prevData => ({
      ...prevData,
      videoLinks: prevData.videoLinks.filter(link => link.id !== linkId)
    }));
  };

  // RUBRIC LOADING
  const handleRubricUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const rubricData = JSON.parse(e.target.result);

        if (!rubricData.assignmentInfo || !rubricData.criteria || !rubricData.rubricLevels) {
          alert('Invalid rubric format. Please ensure the rubric contains assignmentInfo, criteria, and rubricLevels.');
          return;
        }

        setSharedRubric(rubricData); // This is the only state update needed now
        alert(`Rubric "${rubricData.assignmentInfo.title}" loaded successfully!`);

        setGradingData(prevData => ({
          ...prevData,
          rubricGrading: {}
        }));
      } catch (error) {
        alert('Error loading rubric: Invalid JSON format');
        console.error('Rubric loading error:', error);
      }
    };
    reader.readAsText(file);
  };

  // Enhanced total score calculation with grade percentage
  const getTotalScore = () => {
    const score = calculateTotalScore();
    const percentage = rubricTotalPoints
      ? Math.round((score.finalScore / rubricTotalPoints) * 100)
      : 0;
    return { ...score, percentage };
  };

  // EXPORT FUNCTIONS
  const exportToHTML = () => {
    const htmlContent = generateStudentReportHTML();
    const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(htmlBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `grade_report_${gradingData.student.name || 'student'}_${gradingData.assignment.name || 'assignment'}.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportToPDF = () => {
    const html = generateStudentReportHTML();
    const printWindow = window.open('', '_blank');
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const exportGradingData = () => {
    const exportData = {
      ...gradingData,
      rubric: sharedRubric,
      totalScore: getTotalScore(),
      exportDate: new Date().toISOString()
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const exportFileDefaultName = `grading-${gradingData.student.name?.replace(/\s+/g, '-') || 'student'}-${new Date().toISOString().split('T')[0]}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // GRADING SESSION HANDLERS
  const handlePreviousStudent = () => {
    if (!sessionActive) {
      alert('Session has expired. Please refresh the page to start a new session.');
      return;
    }

    if (currentStudent?.id) {
      saveDraft(currentStudent.id, localGradingData);
    }
    const success = previousStudentInSession();
    if (!success) {
      console.log('Already at first student or no active session');
    }
  };

  const handleNextStudent = () => {
    if (!sessionActive) {
      alert('Session has expired. Please refresh the page to start a new session.');
      return;
    }

    if (currentStudent?.id) {
      saveDraft(currentStudent.id, localGradingData);
    }
    const success = nextStudentInSession();
    if (!success) {
      console.log('Already at last student or no active session');
    }
  };

  const handleGoToClassManager = () => {
    setActiveTab('class-manager');
  };

  // PRIVACY: Enhanced saving handlers with session checks
  const handleSaveDraft = () => {
    if (!sessionActive) {
      alert('Session has expired. Cannot save data. Please refresh the page to start a new session.');
      return;
    }

    if (!currentStudent?.id) {
      alert('No student selected for saving draft');
      return;
    }

    saveDraft(currentStudent.id, gradingData);
    alert(`Draft saved for ${currentStudent.name || 'current student'}`);
  };

  const handleNextStudentAsDraft = () => {
    if (!sessionActive) {
      alert('Session has expired. Please refresh the page to start a new session.');
      return;
    }

    if (currentStudent?.id) {
      console.log('💾 Saving as draft for student:', currentStudent.name);
      saveDraft(currentStudent.id, localGradingData);
    }
    const success = nextStudentInSession('draft');
    if (!success) {
      alert('Grading session completed! All students have been graded.');
      setActiveTab('class-manager');
    } else {
      resetGradingForm();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      console.log('✅ Draft saved, moved to next student (and scrolled to top)');
    }
  };

  const getCurrentStudentInfo = () => {
    const students = gradingSession?.classListData?.students;
    if (!gradingSession?.active || !students || !currentStudent) {
      return null;
    }

    const totalStudents = students.length;
    const matchedIndex = students.findIndex((student) => student?.id === currentStudent?.id);
    const currentIndex = matchedIndex >= 0 ? matchedIndex : (gradingSession.currentStudentIndex || 0);

    return {
      student: currentStudent,
      position: currentIndex + 1,
      total: totalStudents,
      isFirst: currentIndex === 0,
      isLast: currentIndex >= totalStudents - 1
    };
  };

  const currentStudentInfo = getCurrentStudentInfo();

  const handleNextStudentAsFinal = () => {
    if (!sessionActive) {
      alert('Session has expired. Please refresh the page to start a new session.');
      return;
    }

    if (currentStudent?.id) {
      console.log('✅ Finalizing grade for student:', currentStudent.name);
      const finalGradeData = {
        ...localGradingData,
        totalScore: getTotalScore(),
        metadata: {
          ...localGradingData.metadata,
          gradedDate: new Date().toISOString(),
          rubricIntegrated: !!sharedRubric
        }
      };
      saveFinalGrade(currentStudent.id, finalGradeData);
      syncGradeToGradeBook(currentStudent.id, finalGradeData.totalScore);
    }
    const success = nextStudentInSession('final');
    if (!success) {
      alert('Grading session completed! All students have been graded.');
      setActiveTab('class-manager');
    } else {
      resetGradingForm();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      console.log('✅ Final grade saved, moved to next student (and scrolled to top)');
    }
  };

  const resetGradingForm = () => {
    const initialRubricGrading = {};
    if (sharedRubric) {
      sharedRubric.criteria.forEach(criterion => {
        initialRubricGrading[criterion.id] = {
          criterionId: criterion.id,
          selectedLevel: null,
          customComments: ''
        };
      });
    }
    setLocalGradingData(prev => ({
      ...prev,
      course: sharedCourseDetails?.course ?? prev.course,
      assignment: sharedCourseDetails?.assignment ?? prev.assignment,
      feedback: { general: '', strengths: '', improvements: '' },
      attachments: [],
      videoLinks: [],
      latePolicy: { penaltyApplied: false, penaltyPercentage: 0, daysLate: 0 },
      metadata: { ...prev.metadata, submittedAt: '', hasApprovedExtension: false, lateOverrideDays: null },
      rubricGrading: initialRubricGrading
    }));
  };

  // PRIVACY: Session warning modal component
  const SessionWarningModal = () => {
    if (!sessionWarning) return null;

    const formatTime = (ms) => {
      const minutes = Math.floor(ms / 60000);
      const seconds = Math.floor((ms % 60000) / 1000);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-mx-4">
          <div className="flex items-center mb-4">
            <Timer className="w-6 h-6 text-orange-500 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              Session Warning
            </h3>
          </div>

          <div className="mb-4">
            <p className="text-gray-700 mb-2">
              {sessionActive
                ? `Your session will expire in ${timeRemaining ? formatTime(timeRemaining) : 'less than 5 minutes'}.`
                : 'Your session has expired for privacy protection.'
              }
            </p>
            <p className="text-sm text-gray-600">
              {sessionActive
                ? 'All data will be automatically cleared when the session expires. Export any work you want to keep.'
                : 'All data has been cleared. Please refresh the page to start a new session.'
              }
            </p>
          </div>

          <div className="flex gap-2">
            {sessionActive ? (
              <>
                <button
                  onClick={() => {
                    extendSession();
                    setSessionWarning(false);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Extend Session
                </button>
                <button
                  onClick={() => setSessionWarning(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  Continue
                </button>
              </>
            ) : (
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Start New Session
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // NEW: No Rubric UI Component with privacy awareness
  const NoRubricUI = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      background: 'linear-gradient(135deg, #fef3c7 0%, #fed7aa 50%, #fde68a 100%)',
      borderRadius: '1rem',
      border: '2px dashed #f59e0b',
      padding: '3rem',
      margin: '2rem 0',
      textAlign: 'center'
    }}>
      <AlertTriangle
        size={80}
        style={{
          color: '#d97706',
          marginBottom: '1.5rem',
          filter: 'drop-shadow(0 2px 4px rgba(217, 119, 6, 0.3))'
        }}
      />

      <h2 style={{
        fontSize: '2rem',
        fontWeight: '700',
        color: '#92400e',
        marginBottom: '1rem',
        textShadow: '0 1px 2px rgba(146, 64, 14, 0.1)'
      }}>
        No Rubric Loaded
      </h2>

      <p style={{
        fontSize: '1.1rem',
        color: '#a16207',
        marginBottom: '2rem',
        maxWidth: '600px',
        lineHeight: '1.6'
      }}>
        A rubric is required before you can begin grading students. Please create a new rubric or load an existing one to get started.
      </p>

      <div style={{
        display: 'flex',
        gap: '1rem',
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        <button
          onClick={() => setActiveTab('rubric-creator')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '1rem 2rem',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '0.75rem',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.5)';
          }}
          onMouseOut={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
          }}
        >
          <FileText size={20} />
          Create New Rubric
        </button>

        <button
          onClick={() => rubricInputRef.current?.click()}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '1rem 2rem',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '0.75rem',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.5)';
          }}
          onMouseOut={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
          }}
        >
          <Upload size={20} />
          Load Existing Rubric
        </button>
      </div>

      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        background: 'rgba(255, 255, 255, 0.7)',
        borderRadius: '0.5rem',
        fontSize: '0.9rem',
        color: '#78350f',
        maxWidth: '500px'
      }}>
        <strong>💡 Tip:</strong> You can also use the AI Rubric Prompt Generator to create rubric prompts for AI tools, or generate assignment prompts before creating your rubric.
      </div>

      {/* PRIVACY: Session notice */}
      {!sessionActive && (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          background: 'rgba(248, 113, 113, 0.1)',
          border: '1px solid #f87171',
          borderRadius: '0.5rem',
          fontSize: '0.9rem',
          color: '#dc2626',
          maxWidth: '500px'
        }}>
          <strong>⚠️ Session Expired:</strong> Your privacy session has ended. Please refresh the page to start a new session.
        </div>
      )}

      <input
        ref={rubricInputRef}
        type="file"
        accept=".json"
        style={{ display: 'none' }}
        onChange={handleRubricUpload}
      />
    </div>
  );

  // If no rubric is loaded, show the No Rubric UI
  if (!sharedRubric) {
    return (
      <>
        <SessionWarningModal />
        <div style={{
          padding: '2rem',
          maxWidth: '1440px',
          margin: '0 auto',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '2rem',
            padding: '1rem',
            background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
            borderRadius: '0.75rem',
            border: '1px solid #d1d5db'
          }}>
            <Settings size={32} style={{ color: '#6b7280' }} />
            <div style={{ flex: 1 }}>
              <h1 style={{
                fontSize: '2rem',
                fontWeight: '700',
                color: '#1f2937',
                margin: 0
              }}>
                🎯 Grading Tool
              </h1>
              <p style={{
                color: '#6b7280',
                margin: 0,
                fontSize: '1rem'
              }}>
                Assessment and feedback management system
              </p>
            </div>
            {/* PRIVACY: Session indicator */}
            <div style={{ textAlign: 'right' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
                color: sessionActive ? '#10b981' : '#ef4444',
                fontWeight: '600'
              }}>
                <Shield size={16} />
                {sessionActive ? 'Privacy Session Active' : 'Session Expired'}
              </div>
              {timeRemaining && sessionActive && (
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {Math.floor(timeRemaining / 60000)}min remaining
                </div>
              )}
            </div>
          </div>

          <NoRubricUI />
        </div>
      </>
    );
  }

  // Regular grading interface when rubric is loaded
  const totalScore = getTotalScore();
  const gradeStatus = currentStudent?.id ? getGradeStatus(currentStudent.id) : 'none';
  const gradeBookProjects = gradeBook?.projects || [];
  const hasGradeBookProjects = gradeBookProjects.length > 0;

  return (
    <>
      <SessionWarningModal />
      {inlineEditor.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b">
              <h4 className="text-xl font-semibold text-gray-800">{inlineEditor.field}</h4>
              <div className="flex gap-3">
                <button
                  onClick={() => closeInlineEditor(true)}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg flex items-center gap-2 text-sm font-medium"
                >
                  <Save size={18} />
                  Save & Close
                </button>
                <button
                  onClick={() => closeInlineEditor(false)}
                  className="px-6 py-3 bg-red-600 text-white rounded-lg flex items-center gap-2 text-sm font-medium"
                >
                  <X size={18} />
                  Cancel
                </button>
              </div>
            </div>
            <div className="p-6 flex-grow overflow-y-auto">
              <TiptapEditor
                ref={editorRef}
                value={inlineEditor.content}
                onChange={(html) => { richTextContentRef.current = html; }}
                placeholder={`Enter details for ${inlineEditor.field}...`}
              />
            </div>
          </div>
        </div>
      )}

      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: 'white',
          borderBottom: '2px solid #e5e7eb',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
        }}
      >
        <div
          style={{
            maxWidth: '1800px',
            margin: '0 auto',
            padding: '1rem 2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '2rem'
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem'
            }}
          >
            <button
              onClick={handleGoToClassManager}
              title="Go to Class Manager"
              aria-label="Go to Class Manager"
              style={{
                border: 'none',
                background: 'white',
                padding: '0.5rem',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                color: '#374151',
                marginTop: '0.15rem'
              }}
            >
              <House size={20} />
            </button>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: '#111827',
                  lineHeight: 1.25,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere'
                }}
              >
                {currentStudent?.name || gradingData.student.name}
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginTop: '0.35rem'
              }}>
                <button
                  onClick={handlePreviousStudent}
                  disabled={!sessionActive || !currentStudentInfo || currentStudentInfo.isFirst}
                  title="Previous Student"
                  aria-label="Previous Student"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #e5e7eb',
                    background: !sessionActive || !currentStudentInfo || currentStudentInfo.isFirst ? '#f9fafb' : '#f3f4f6',
                    padding: '0.15rem',
                    borderRadius: '0.375rem',
                    cursor: !sessionActive || !currentStudentInfo || currentStudentInfo.isFirst ? 'not-allowed' : 'pointer',
                    color: !sessionActive || !currentStudentInfo || currentStudentInfo.isFirst ? '#d1d5db' : '#6b7280',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <ChevronLeft size={16} />
                </button>
                <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 500, whiteSpace: 'nowrap' }}>
                  {currentStudentInfo?.position || 1} / {currentStudentInfo?.total || 1}
                </span>
                <button
                  onClick={handleNextStudent}
                  disabled={!sessionActive || !currentStudentInfo || currentStudentInfo.isLast}
                  title="Next Student"
                  aria-label="Next Student"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #e5e7eb',
                    background: !sessionActive || !currentStudentInfo || currentStudentInfo.isLast ? '#f9fafb' : '#f3f4f6',
                    padding: '0.15rem',
                    borderRadius: '0.375rem',
                    cursor: !sessionActive || !currentStudentInfo || currentStudentInfo.isLast ? 'not-allowed' : 'pointer',
                    color: !sessionActive || !currentStudentInfo || currentStudentInfo.isLast ? '#d1d5db' : '#6b7280',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>

          <div style={{
            minWidth: '360px',
            display: 'flex',
            flexDirection: 'row',
            gap: '1.5rem',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.35rem', whiteSpace: 'nowrap' }}>
                Criteria Completed: {completionPercentage}%
              </div>
              <div
                style={{
                  width: '100%',
                  height: '10px',
                  background: '#e5e7eb',
                  borderRadius: '9999px',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    width: `${completionPercentage}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #3b82f6, #10b981)',
                    transition: 'width 0.3s ease'
                  }}
                />
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.35rem', whiteSpace: 'nowrap' }}>
                Class Finalized: {classCompletionPercentage}%
              </div>
              <div
                style={{
                  width: '100%',
                  height: '10px',
                  background: '#e5e7eb',
                  borderRadius: '9999px',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    width: `${classCompletionPercentage}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #8b5cf6, #6366f1)',
                    transition: 'width 0.3s ease'
                  }}
                />
              </div>
            </div>
            <div style={{
              minWidth: '240px',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.4rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
                color: '#6b7280',
                fontSize: '0.9rem',
                fontWeight: 600
              }}>
                <FileText size={16} color="#374151" />
                Grade Book Project
              </div>
              <select
                value={selectedProjectId || 'na'}
                onChange={(e) => updateSelectedProject(e.target.value === 'na' ? null : e.target.value)}
                disabled={!sessionActive || !hasGradeBookProjects}
                style={{
                  padding: '0.6rem 0.75rem',
                  borderRadius: '0.65rem',
                  border: '1px solid #d1d5db',
                  background: sessionActive && hasGradeBookProjects ? 'white' : '#f3f4f6',
                  color: sessionActive && hasGradeBookProjects ? '#111827' : '#9ca3af',
                  fontWeight: 600,
                  cursor: sessionActive && hasGradeBookProjects ? 'pointer' : 'not-allowed'
                }}
              >
                <option value="na">
                  N/A - {hasGradeBookProjects ? 'Do not sync to a project' : 'Create a grade book first'}
                </option>
                {gradeBookProjects.map((project) => (
                  <option key={project.id || project.name} value={project.id}>
                    {project.name || 'Untitled Project'}
                  </option>
                ))}
              </select>
              {!hasGradeBookProjects && (
                <div style={{ fontSize: '0.85rem', color: '#f97316', fontWeight: 600 }}>
                  No grade book projects found. Create a grade book to map grades to a project column.
                </div>
              )}
            </div>
          </div>

          {sessionActive && timeRemaining && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: '#f59e0b', fontWeight: 600 }}>
              <Timer size={16} /> {Math.floor(timeRemaining / 60000)}min remaining
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={handleSaveDraft}
              disabled={!sessionActive}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: sessionActive ? '#f3f4f6' : '#e5e7eb',
                color: sessionActive ? '#374151' : '#9ca3af',
                padding: '0.65rem 1rem',
                borderRadius: '0.75rem',
                border: '1px solid #e5e7eb',
                cursor: sessionActive ? 'pointer' : 'not-allowed',
                fontWeight: 600
              }}
            >
              <Save size={16} /> Save Draft
            </button>
            <button
              onClick={handleNextStudentAsFinal}
              disabled={!sessionActive}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: sessionActive ? 'linear-gradient(135deg, #10b981, #059669)' : '#9ca3af',
                color: 'white',
                padding: '0.65rem 1rem',
                borderRadius: '0.75rem',
                border: 'none',
                cursor: sessionActive ? 'pointer' : 'not-allowed',
                fontWeight: 700,
                boxShadow: sessionActive ? '0 4px 12px rgba(16, 185, 129, 0.35)' : 'none'
              }}
            >
              <CheckCircle size={16} /> Finalize & Next
            </button>
          </div>
        </div>
      </header>

      <div
        style={{
          maxWidth: '1800px',
          margin: '0 auto',
          padding: '2rem',
          display: 'flex',
          gap: '1.5rem',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}
      >
        {showLeftSidebar && (
          <aside style={{ width: '340px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div
              style={{
                background: 'white',
                borderRadius: '1rem',
                padding: '1.5rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              <h3
                style={{
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '1rem'
                }}
              >
                👤 Student
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Name</div>
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1f2937' }}>
                    {gradingData.student.name}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>ID</div>
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1f2937' }}>
                    {gradingData.student.id}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Email</div>
                  <div style={{ fontSize: '0.875rem', color: '#3b82f6' }}>
                    {gradingData.student.email}
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                background: 'white',
                borderRadius: '1rem',
                padding: '1.5rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              <h3
                style={{
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '1rem'
                }}
              >
                📝 Assignment Snapshot
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Course</div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>
                    {gradingData.course.code} {gradingData.course.name}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Assignment</div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>
                    {gradingData.assignment.name}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                    Due: {gradingData.assignment.dueDate || gradingData.assignment.dueAt || 'N/A'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Max Points</div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>
                    {gradingData.assignment.maxPoints || rubricTotalPoints}
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                background: 'white',
                borderRadius: '1rem',
                padding: '1.5rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border:
                  gradingData.metadata?.lateOverrideDays !== null ? '2px solid #f59e0b' : '2px solid transparent'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <Clock size={18} style={{ color: '#f59e0b' }} />
                <h3
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: '700',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    margin: 0
                  }}
                >
                  ⏱ Submission Timing
                </h3>
              </div>

              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '1rem' }}>
                Active Policy: <strong>{effectiveLatePolicy.name}</strong>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                {submissionTimingOptions.map((option, idx) => (
                  <label
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem',
                      background:
                        (option.value === null
                          ? gradingData.metadata?.lateOverrideDays === null || gradingData.metadata?.lateOverrideDays === undefined
                          : gradingData.metadata?.lateOverrideDays === option.value)
                          ? '#fff7ed'
                          : '#f9fafb',
                      border: `2px solid ${
                        (option.value === null
                        ? gradingData.metadata?.lateOverrideDays === null || gradingData.metadata?.lateOverrideDays === undefined
                        : gradingData.metadata?.lateOverrideDays === option.value)
                          ? '#f59e0b'
                          : '#e5e7eb'
                      }`,
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    <input
                      type="radio"
                      name="late-override"
                      checked={
                        option.value === null
                          ? gradingData.metadata?.lateOverrideDays === null || gradingData.metadata?.lateOverrideDays === undefined
                          : gradingData.metadata?.lateOverrideDays === option.value
                      }
                      onChange={() =>
                        setGradingData((prev) => ({
                          ...prev,
                          metadata: { ...prev.metadata, lateOverrideDays: option.value }
                        }))
                      }
                      disabled={!sessionActive}
                      style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem',
                  background: gradingData.metadata?.hasApprovedExtension ? '#ecfdf5' : '#f9fafb',
                  border: `2px solid ${gradingData.metadata?.hasApprovedExtension ? '#10b981' : '#e5e7eb'}`,
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                <input
                  type="checkbox"
                  checked={gradingData.metadata?.hasApprovedExtension || false}
                  onChange={(e) =>
                    setGradingData((prev) => ({
                      ...prev,
                      metadata: { ...prev.metadata, hasApprovedExtension: e.target.checked }
                    }))
                  }
                  disabled={!sessionActive}
                  style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#10b981' }}
                />
                <span>Extension approved (≥24h before due)</span>
              </label>

              {scoreSummary.penaltyApplied && (
                <div
                  style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    background: '#fef3c7',
                    border: '1px solid #fbbf24',
                    borderRadius: '0.5rem',
                    fontSize: '0.75rem',
                    color: '#92400e'
                  }}
                >
                  <strong>⚠️ Penalty Applied:</strong>
                  <br />
                  {Math.round(scoreSummary.penaltyPercentage * 100)}% deduction
                </div>
              )}
            </div>

            {gradingSession && gradingSession.classListData?.students && (
              <div
                style={{
                  background: 'white',
                  borderRadius: '1rem',
                  padding: '1.25rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Users size={18} />
                  <div style={{ fontWeight: 700 }}>Batch Session Active</div>
                </div>
                <div style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '0.75rem' }}>
                  Student {gradingSession.currentStudentIndex + 1} of {gradingSession.classListData.students.length}:<br />
                  <strong>{gradingSession.classListData.students[gradingSession.currentStudentIndex]?.name || 'Unknown'}</strong>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={handlePreviousStudent}
                    disabled={gradingSession.currentStudentIndex === 0 || !sessionActive}
                    style={{
                      flex: 1,
                      padding: '0.6rem 0.75rem',
                      background:
                        gradingSession.currentStudentIndex === 0 || !sessionActive ? '#f3f4f6' : '#10b981',
                      color:
                        gradingSession.currentStudentIndex === 0 || !sessionActive ? '#9ca3af' : 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor:
                        gradingSession.currentStudentIndex === 0 || !sessionActive ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <SkipBack size={16} /> Previous
                  </button>
                  <button
                    onClick={handleNextStudent}
                    disabled={gradingSession.currentStudentIndex >= gradingSession.classListData.students.length - 1 || !sessionActive}
                    style={{
                      flex: 1,
                      padding: '0.6rem 0.75rem',
                      background:
                        gradingSession.currentStudentIndex >= gradingSession.classListData.students.length - 1 || !sessionActive
                          ? '#f3f4f6'
                          : '#3b82f6',
                      color:
                        gradingSession.currentStudentIndex >= gradingSession.classListData.students.length - 1 || !sessionActive
                          ? '#9ca3af'
                          : 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor:
                        gradingSession.currentStudentIndex >= gradingSession.classListData.students.length - 1 || !sessionActive
                          ? 'not-allowed'
                          : 'pointer'
                    }}
                  >
                    Next <SkipForward size={16} />
                  </button>
                </div>
              </div>
            )}

            <div
              style={{
                background: 'white',
                borderRadius: '1rem',
                padding: '1.5rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              <h3
                style={{
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '1rem'
                }}
              >
                💾 Export Options
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <button
                  onClick={exportToHTML}
                  disabled={!sessionActive}
                  style={{
                    padding: '0.75rem',
                    background: sessionActive ? '#3b82f6' : '#9ca3af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: sessionActive ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <FileDown size={16} /> Export to HTML
                </button>
                <button
                  onClick={exportToPDF}
                  disabled={!sessionActive}
                  style={{
                    padding: '0.75rem',
                    background: sessionActive ? '#dc2626' : '#9ca3af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: sessionActive ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <FileText size={16} /> Export to PDF
                </button>
                <button
                  onClick={exportGradingData}
                  disabled={!sessionActive}
                  style={{
                    padding: '0.75rem',
                    background: sessionActive ? '#6b7280' : '#9ca3af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: sessionActive ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Download size={16} /> Export to JSON
                </button>
              </div>
            </div>
          </aside>
        )}

        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {sharedRubric && (
            <div
              style={{
                background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                border: '1px solid #93c5fd',
                borderRadius: '0.75rem',
                padding: '1.5rem'
              }}
            >
              <h2
                style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#1e40af',
                  margin: '0 0 0.5rem 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <FileText size={20} /> Current Rubric: {sharedRubric.assignmentInfo.title}
              </h2>
              <p style={{ color: '#1e40af', margin: 0, fontSize: '0.9rem' }}>
                {sharedRubric.assignmentInfo.description || 'No description provided'}
              </p>
              <div
                style={{
                  display: 'flex',
                  gap: '2rem',
                  marginTop: '0.75rem',
                  fontSize: '0.875rem',
                  color: '#1d4ed8',
                  flexWrap: 'wrap'
                }}
              >
                <span><strong>Weight:</strong> {sharedRubric.assignmentInfo?.weight || 0}%</span>
                <span><strong>Total Points:</strong> {sharedRubric.assignmentInfo?.totalPoints || 0}</span>
                <span><strong>Passing:</strong> {sharedRubric.assignmentInfo?.passingThreshold || 0}%</span>
                <span><strong>Criteria:</strong> {sharedRubric.criteria?.length || 0}</span>
              </div>
            </div>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '1rem'
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '1rem',
                padding: '1.25rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1f2937', margin: 0 }}>
                  🏫 Course Information
                </h3>
                {classList && classList.courseMetadata && (
                  <button
                    onClick={pullCourseDataFromClassList}
                    disabled={!sessionActive}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.5rem 0.75rem',
                      background: sessionActive ? '#3b82f6' : '#9ca3af',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '0.75rem',
                      cursor: sessionActive ? 'pointer' : 'not-allowed'
                    }}
                  >
                    <Download size={14} />
                    Pull Course Data
                  </button>
                )}
              </div>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <input
                  type="text"
                  value={gradingData.course.code}
                  onChange={(e) =>
                    setGradingData((prevData) => ({
                      ...prevData,
                      course: { ...prevData.course, code: e.target.value }
                    }))
                  }
                  disabled={!sessionActive}
                  placeholder="Course Code"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    backgroundColor: sessionActive ? 'white' : '#f3f4f6'
                  }}
                />
                <input
                  type="text"
                  value={gradingData.course.name}
                  onChange={(e) =>
                    setGradingData((prevData) => ({
                      ...prevData,
                      course: { ...prevData.course, name: e.target.value }
                    }))
                  }
                  disabled={!sessionActive}
                  placeholder="Course Name"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    backgroundColor: sessionActive ? 'white' : '#f3f4f6'
                  }}
                />
                <div>
                  <input
                    type="text"
                    value={gradingData.course.instructor}
                    onChange={(e) =>
                      setGradingData((prevData) => ({
                        ...prevData,
                        course: { ...prevData.course, instructor: e.target.value }
                      }))
                    }
                    disabled={!sessionActive}
                    placeholder="Course Professor"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      backgroundColor: sessionActive ? 'white' : '#f3f4f6'
                    }}
                  />
                  <span style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.2rem', display: 'block' }}>
                    Course Professor
                  </span>
                </div>
                <div>
                  <input
                    type="text"
                    value={gradingData.course.evaluator || ''}
                    onChange={(e) =>
                      setGradingData((prevData) => ({
                        ...prevData,
                        course: { ...prevData.course, evaluator: e.target.value }
                      }))
                    }
                    disabled={!sessionActive}
                    placeholder="Course Evaluator"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      backgroundColor: sessionActive ? 'white' : '#f3f4f6'
                    }}
                  />
                  <span style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.2rem', display: 'block' }}>
                    Course Evaluator
                  </span>
                </div>
                <input
                  type="text"
                  value={gradingData.course.term}
                  onChange={(e) =>
                    setGradingData((prevData) => ({
                      ...prevData,
                      course: { ...prevData.course, term: e.target.value }
                    }))
                  }
                  disabled={!sessionActive}
                  placeholder="Term"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    backgroundColor: sessionActive ? 'white' : '#f3f4f6'
                  }}
                />
              </div>
            </div>

            <div
              style={{
                background: 'white',
                borderRadius: '1rem',
                padding: '1.25rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}
            >
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1f2937', marginBottom: '0.75rem' }}>
                📋 Assignment Information
              </h3>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <input
                  type="text"
                  value={gradingData.assignment.name}
                  onChange={(e) =>
                    setGradingData((prevData) => ({
                      ...prevData,
                      assignment: { ...prevData.assignment, name: e.target.value }
                    }))
                  }
                  disabled={!sessionActive}
                  placeholder="Assignment Name"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    backgroundColor: sessionActive ? 'white' : '#f3f4f6'
                  }}
                />
                <input
                  type="datetime-local"
                  value={gradingData.assignment.dueAt || gradingData.assignment.dueDate}
                  onChange={(e) =>
                    setGradingData((prevData) => ({
                      ...prevData,
                      assignment: {
                        ...prevData.assignment,
                        dueAt: e.target.value,
                        dueDate: e.target.value?.split('T')?.[0] || prevData.assignment.dueDate
                      }
                    }))
                  }
                  disabled={!sessionActive}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    backgroundColor: sessionActive ? 'white' : '#f3f4f6'
                  }}
                />
                <input
                  type="number"
                  value={gradingData.assignment.maxPoints}
                  onChange={(e) =>
                    setGradingData((prevData) => ({
                      ...prevData,
                      assignment: { ...prevData.assignment, maxPoints: parseFloat(e.target.value) || 0 }
                    }))
                  }
                  disabled={!sessionActive}
                  placeholder="Max Points"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    backgroundColor: sessionActive ? 'white' : '#f3f4f6'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Criteria Cards - Center Panel */}
          {sharedRubric &&
            sharedRubric.criteria &&
            sharedRubric.criteria.map((criterion, idx) => {
              const currentGrading = gradingData.rubricGrading[criterion.id];
              const selectedLevel = currentGrading?.selectedLevel;
              const isCompleted = !!selectedLevel;
              const isExpanded = expandedCriteria[criterion.id];

              return (
                <div
                  key={criterion.id}
                  style={{
                    background: 'white',
                    borderRadius: '1rem',
                    padding: '1.5rem',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: isCompleted ? '2px solid #10b981' : '2px solid transparent',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '1.5rem'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '32px',
                          height: '32px',
                          background: isCompleted ? '#10b981' : '#e5e7eb',
                          color: isCompleted ? 'white' : '#6b7280',
                          borderRadius: '50%',
                          fontSize: '0.875rem',
                          fontWeight: '700',
                          flexShrink: 0
                        }}
                      >
                        {isCompleted ? '✓' : idx + 1}
                      </span>

                      <div style={{ flex: 1 }}>
                        <h3
                          style={{
                            fontSize: '1.125rem',
                            fontWeight: '700',
                            color: '#1f2937',
                            margin: 0
                          }}
                        >
                          {criterion.name}
                        </h3>
                        <p
                          style={{
                            fontSize: '0.875rem',
                            color: '#6b7280',
                            margin: '0.25rem 0 0 0'
                          }}
                        >
                          {criterion.description || ''}
                        </p>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#f0f9ff',
                          borderRadius: '0.5rem',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          color: '#1e40af'
                        }}
                      >
                        {criterion.maxPoints} points
                      </div>

                      <button
                        onClick={() => setExpandedCriteria({ ...expandedCriteria, [criterion.id]: !isExpanded })}
                        style={{
                          padding: '0.5rem',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#6b7280',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </button>
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${getLevelsForCriterion(criterion).length}, 1fr)`,
                      gap: '0.75rem',
                      marginBottom: '1rem'
                    }}
                  >
                    {getLevelsForCriterion(criterion).map((level) => {
                      const isSelected = currentGrading?.selectedLevel === level.level;
                      const levelColor = level.color || '#2563eb';
                      const points = calculateLevelPoints({
                        criterion,
                        levelKey: level.level,
                        rubricLevels: getLevelsForCriterion(criterion)
                      });

                      return (
                        <button
                          key={level.level}
                          onClick={() => updateRubricGrading(criterion.id, level.level)}
                          disabled={!sessionActive}
                          style={{
                            padding: '1rem',
                            background: isSelected ? levelColor : '#f9fafb',
                            border: isSelected ? `2px solid ${levelColor}` : '2px solid #e5e7eb',
                            borderRadius: '0.75rem',
                            cursor: sessionActive ? 'pointer' : 'not-allowed',
                            transition: 'all 0.2s ease',
                            textAlign: 'center',
                            position: 'relative',
                            opacity: sessionActive ? 1 : 0.6,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.35rem',
                            lineHeight: 1.2,
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            minHeight: '110px'
                          }}
                        >
                          {isSelected && (
                            <div style={{ position: 'absolute', top: '0.5rem', right: '0.5rem' }}>
                              <CheckCircle size={20} style={{ color: 'white' }} />
                            </div>
                          )}
                          <div
                            style={{
                              fontSize: '0.85rem',
                              fontWeight: '700',
                              color: isSelected ? 'white' : '#374151',
                              marginBottom: '0.5rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              width: '100%'
                            }}
                          >
                            {level.name}
                          </div>
                          <div
                            style={{
                              fontSize: '1.5rem',
                              fontWeight: '700',
                              color: isSelected ? 'white' : levelColor
                            }}
                          >
                            {level.level === 'incomplete' ? '0' : points}
                          </div>
                          <div
                            style={{
                              fontSize: '0.75rem',
                              color: isSelected ? 'rgba(255,255,255,0.8)' : '#6b7280',
                              marginTop: '0.25rem'
                            }}
                          >
                            points
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {selectedLevel && (
                    <div
                      style={{
                        background: '#f0f9ff',
                        border: '2px solid #3b82f6',
                        borderRadius: '0.75rem',
                        padding: '1rem',
                        marginBottom: '1rem'
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem',
                          flexWrap: 'wrap'
                        }}
                      >
                        <label
                          style={{
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            color: '#1e40af',
                            minWidth: '120px'
                          }}
                        >
                          🎯 Adjust Points:
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: '1' }}>
                          <input
                            type="number"
                            min={(() => {
                              const { min } = getValidPointRange({
                                criterion,
                                levelKey: selectedLevel,
                                rubricLevels: getLevelsForCriterion(criterion)
                              });
                              return min;
                            })()}
                            max={(() => {
                              const { max } = getValidPointRange({
                                criterion,
                                levelKey: selectedLevel,
                                rubricLevels: getLevelsForCriterion(criterion)
                              });
                              return max;
                            })()}
                            value={(() => {
                              const customPoints = currentGrading?.customPoints;
                              if (customPoints !== null && customPoints !== undefined) {
                                return customPoints;
                              }
                              return calculateLevelPoints({
                                criterion,
                                levelKey: selectedLevel,
                                rubricLevels: getLevelsForCriterion(criterion)
                              });
                            })()}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '') {
                                // Reset to base value
                                updateCustomPoints(criterion.id, null);
                              } else {
                                updateCustomPoints(criterion.id, Number(value));
                              }
                            }}
                            disabled={!sessionActive}
                            style={{
                              width: '80px',
                              padding: '0.5rem',
                              border: '2px solid #3b82f6',
                              borderRadius: '0.5rem',
                              fontSize: '1rem',
                              fontWeight: '600',
                              textAlign: 'center',
                              background: 'white'
                            }}
                          />
                          <span
                            style={{
                              fontSize: '0.75rem',
                              color: '#6b7280',
                              fontWeight: '500'
                            }}
                          >
                            Valid range: {(() => {
                              const { min, max } = getValidPointRange({
                                criterion,
                                levelKey: selectedLevel,
                                rubricLevels: getLevelsForCriterion(criterion)
                              });
                              return `${min}–${max}`;
                            })()}
                          </span>
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: '0.7rem',
                          color: '#6b7280',
                          marginTop: '0.5rem',
                          fontStyle: 'italic'
                        }}
                      >
                        Fine-tune the score within this rubric level's range
                      </div>
                    </div>
                  )}

                  {isExpanded && (
                    <div
                      style={{
                        background: '#f9fafb',
                        borderRadius: '0.75rem',
                        padding: '1rem',
                        marginBottom: '1rem'
                      }}
                    >
                      <h4
                        style={{
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '0.75rem'
                        }}
                      >
                        Level Descriptions:
                      </h4>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: '0.75rem'
                        }}
                      >
                        {getLevelsForCriterion(criterion).map((level) => {
                          const levelDef = criterion.levels?.[level.level];
                          const levelColor = level.color || '#2563eb';
                          const levelPoints = calculateLevelPoints({
                            criterion,
                            levelKey: level.level,
                            rubricLevels: getLevelsForCriterion(criterion)
                          });
                          return (
                            <div
                              key={level.level}
                              style={{
                                background: 'white',
                                borderRadius: '0.5rem',
                                padding: '0.75rem',
                                border: '1px solid #e5e7eb',
                                borderLeft: `4px solid ${levelColor}`
                              }}
                            >
                              <div
                                style={{
                                  fontSize: '0.875rem',
                                  fontWeight: '600',
                                  color: levelColor,
                                  marginBottom: '0.25rem'
                                }}
                              >
                                {level.name} ({levelPoints} pts)
                              </div>
                              <div
                                style={{
                                  fontSize: '0.75rem',
                                  color: '#6b7280',
                                  lineHeight: 1.5
                                }}
                              >
                                {renderFormattedContent(levelDef?.description || '')}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {selectedLevel && (
                    <div style={{ marginTop: '1rem' }}>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '0.5rem'
                        }}
                      >
                        💬 Feedback for this criterion:
                      </label>

                      {criterion.feedbackLibrary && sessionActive && (
                        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                          {Object.entries(criterion.feedbackLibrary).map(([category, comments]) => (
                            <select
                              key={category}
                              onChange={(e) => {
                                if (e.target.value) {
                                  addCriterionFeedback(criterion.id, e.target.value);
                                  e.target.value = '';
                                }
                              }}
                              defaultValue=""
                              style={{
                                padding: '0.5rem 0.75rem',
                                border: '2px solid #d1d5db',
                                borderRadius: '0.5rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                background: 'white'
                              }}
                            >
                              <option value="">+ {category}</option>
                              {comments.map((comment, idx) => (
                                <option key={idx} value={comment}>
                                  {renderFormattedContent(comment)}
                                </option>
                              ))}
                            </select>
                          ))}
                        </div>
                      )}

                      <div style={{ position: 'relative' }}>
                        <textarea
                          value={renderFormattedContent(currentGrading?.customComments || '', { trim: false })}
                          onChange={(e) =>
                            updateRubricGrading(criterion.id, currentGrading?.selectedLevel, e.target.value)
                          }
                          disabled={!sessionActive}
                          placeholder="Add additional comments for this criterion, or select from library above..."
                          style={{
                            width: '100%',
                            minHeight: '100px',
                            padding: '0.75rem',
                            paddingRight: '2.75rem',
                            border: '2px solid #d1d5db',
                            borderRadius: '0.5rem',
                            fontSize: '0.875rem',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            backgroundColor: sessionActive ? 'white' : '#f3f4f6',
                            cursor: sessionActive ? 'text' : 'not-allowed'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            openInlineEditor(
                              currentGrading?.customComments || '',
                              `Feedback for ${criterion.name || criterion.title || 'criterion'}`,
                              (newContent) => {
                                updateRubricGrading(criterion.id, currentGrading?.selectedLevel, newContent);
                              }
                            )
                          }
                          disabled={!sessionActive}
                          aria-label="Expand feedback editor"
                          style={{
                            position: 'absolute',
                            top: '0.5rem',
                            right: '0.5rem',
                            border: '1px solid #d1d5db',
                            background: sessionActive ? 'white' : '#f3f4f6',
                            borderRadius: '0.375rem',
                            padding: '0.35rem',
                            cursor: sessionActive ? 'pointer' : 'not-allowed',
                            color: '#2563eb'
                          }}
                        >
                          <Maximize2 size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

        {/* Overall Feedback */}
        <div
          style={{
            background: 'white',
            borderRadius: '1rem',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
          <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '1rem', color: '#111827' }}>
            🧠 Overall Feedback
          </h3>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <div style={{ position: 'relative' }}>
              <textarea
                value={renderFormattedContent(gradingData.feedback.general, { trim: false })}
                onChange={(e) =>
                  setGradingData((prevData) => ({
                    ...prevData,
                    feedback: { ...prevData.feedback, general: e.target.value }
                  }))
                }
                disabled={!sessionActive}
                placeholder="Overall feedback"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  paddingRight: '2.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  minHeight: '100px',
                  backgroundColor: sessionActive ? 'white' : '#f3f4f6'
                }}
              />
              <button
                type="button"
                onClick={() =>
                  openInlineEditor(gradingData.feedback.general, 'Overall feedback', (newContent) =>
                    setGradingData((prevData) => ({
                      ...prevData,
                      feedback: { ...prevData.feedback, general: newContent }
                    }))
                  )
                }
                disabled={!sessionActive}
                aria-label="Expand overall feedback editor"
                style={{
                  position: 'absolute',
                  top: '0.5rem',
                  right: '0.5rem',
                  border: '1px solid #d1d5db',
                  background: sessionActive ? 'white' : '#f3f4f6',
                  borderRadius: '0.375rem',
                  padding: '0.35rem',
                  cursor: sessionActive ? 'pointer' : 'not-allowed',
                  color: '#2563eb'
                }}
              >
                <Maximize2 size={16} />
              </button>
            </div>
            <div style={{ position: 'relative' }}>
              <textarea
                value={renderFormattedContent(gradingData.feedback.strengths, { trim: false })}
                onChange={(e) =>
                  setGradingData((prevData) => ({
                    ...prevData,
                    feedback: { ...prevData.feedback, strengths: e.target.value }
                  }))
                }
                disabled={!sessionActive}
                placeholder="Key strengths"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  paddingRight: '2.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  minHeight: '80px',
                  backgroundColor: sessionActive ? 'white' : '#f3f4f6'
                }}
              />
              <button
                type="button"
                onClick={() =>
                  openInlineEditor(gradingData.feedback.strengths, 'Key strengths', (newContent) =>
                    setGradingData((prevData) => ({
                      ...prevData,
                      feedback: { ...prevData.feedback, strengths: newContent }
                    }))
                  )
                }
                disabled={!sessionActive}
                aria-label="Expand key strengths editor"
                style={{
                  position: 'absolute',
                  top: '0.5rem',
                  right: '0.5rem',
                  border: '1px solid #d1d5db',
                  background: sessionActive ? 'white' : '#f3f4f6',
                  borderRadius: '0.375rem',
                  padding: '0.35rem',
                  cursor: sessionActive ? 'pointer' : 'not-allowed',
                  color: '#2563eb'
                }}
              >
                <Maximize2 size={16} />
              </button>
            </div>
            <div style={{ position: 'relative' }}>
              <textarea
                value={renderFormattedContent(gradingData.feedback.improvements, { trim: false })}
                onChange={(e) =>
                  setGradingData((prevData) => ({
                    ...prevData,
                    feedback: { ...prevData.feedback, improvements: e.target.value }
                  }))
                }
                disabled={!sessionActive}
                placeholder="Areas for improvement"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  paddingRight: '2.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  minHeight: '80px',
                  backgroundColor: sessionActive ? 'white' : '#f3f4f6'
                }}
              />
              <button
                type="button"
                onClick={() =>
                  openInlineEditor(gradingData.feedback.improvements, 'Areas for improvement', (newContent) =>
                    setGradingData((prevData) => ({
                      ...prevData,
                      feedback: { ...prevData.feedback, improvements: newContent }
                    }))
                  )
                }
                disabled={!sessionActive}
                aria-label="Expand areas for improvement editor"
                style={{
                  position: 'absolute',
                  top: '0.5rem',
                  right: '0.5rem',
                  border: '1px solid #d1d5db',
                  background: sessionActive ? 'white' : '#f3f4f6',
                  borderRadius: '0.375rem',
                  padding: '0.35rem',
                  cursor: sessionActive ? 'pointer' : 'not-allowed',
                  color: '#2563eb'
                }}
              >
                <Maximize2 size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Video Links */}
        <div
          style={{
            background: 'white',
            borderRadius: '1rem',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            opacity: sessionActive ? 1 : 0.6
          }}
        >
          <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '1rem', color: '#111827' }}>
            🎥 Video Links
          </h3>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <input
              type="url"
              value={videoLinkInput}
              onChange={(e) => setVideoLinkInput(e.target.value)}
              disabled={!sessionActive}
              placeholder="Enter video URL (YouTube, Vimeo, etc.)"
              style={{
                flex: 1,
                minWidth: '240px',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                backgroundColor: sessionActive ? 'white' : '#f3f4f6',
                cursor: sessionActive ? 'text' : 'not-allowed'
              }}
            />
            <input
              type="text"
              value={videoTitle}
              onChange={(e) => setVideoTitle(e.target.value)}
              disabled={!sessionActive}
              placeholder="Video title (optional)"
              style={{
                width: '220px',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                backgroundColor: sessionActive ? 'white' : '#f3f4f6',
                cursor: sessionActive ? 'text' : 'not-allowed'
              }}
            />
            <button
              onClick={addVideoLink}
              disabled={!videoLinkInput.trim() || !sessionActive}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                background: videoLinkInput.trim() && sessionActive ? '#10b981' : '#d1d5db',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: videoLinkInput.trim() && sessionActive ? 'pointer' : 'not-allowed',
                fontSize: '0.875rem',
                fontWeight: '600'
              }}
            >
              <Plus size={16} /> Add Link
            </button>
          </div>
          {gradingData.videoLinks && gradingData.videoLinks.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {gradingData.videoLinks.map((link) => (
                <div
                  key={link.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem',
                    background: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem'
                  }}
                >
                  <Video size={16} style={{ color: '#6b7280' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', fontSize: '0.875rem', color: '#374151' }}>{link.title}</div>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '0.75rem', color: '#3b82f6', textDecoration: 'none' }}
                    >
                      {link.url}
                    </a>
                  </div>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{link.addedDate}</span>
                  <button
                    onClick={() => removeVideoLink(link.id)}
                    disabled={!sessionActive}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: sessionActive ? '#ef4444' : '#9ca3af',
                      cursor: sessionActive ? 'pointer' : 'not-allowed',
                      padding: '0.25rem'
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Image Feedback Attachments */}
        <div
          style={{
            background: 'white',
            borderRadius: '1rem',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            opacity: sessionActive ? 1 : 0.6
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1rem'
            }}
          >
            <h3 style={{ fontSize: '1.125rem', fontWeight: '700', margin: 0, color: '#111827' }}>
              🖼️ Image Feedback
            </h3>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.65rem 1rem',
                background: sessionActive ? '#3b82f6' : '#d1d5db',
                color: 'white',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: sessionActive ? 'pointer' : 'not-allowed'
              }}
            >
              <Upload size={16} />
              Upload Images
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileUpload}
                disabled={!sessionActive}
                style={{ display: 'none' }}
              />
            </label>
          </div>

          {gradingData.attachments && gradingData.attachments.length > 0 ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: '1rem'
              }}
            >
              {gradingData.attachments.map((att) => (
                <div
                  key={att.id}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.75rem',
                    padding: '0.75rem',
                    background: '#f9fafb',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem'
                  }}
                >
                  {att.base64Data ? (
                    <img
                      src={att.base64Data}
                      alt={att.name}
                      style={{
                        width: '100%',
                        height: '140px',
                        objectFit: 'cover',
                        borderRadius: '0.5rem',
                        border: '1px solid #e5e7eb'
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        height: '140px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#fff',
                        border: '1px dashed #d1d5db',
                        borderRadius: '0.5rem',
                        color: '#6b7280',
                        fontSize: '0.875rem'
                      }}
                    >
                      {att.name}
                    </div>
                  )}
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>
                    {att.name}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    {(att.size / 1024).toFixed(1)} KB
                  </div>
                  <button
                    onClick={() => removeAttachment(att.id)}
                    disabled={!sessionActive}
                    style={{
                      marginTop: 'auto',
                      padding: '0.5rem',
                      background: sessionActive ? '#ef4444' : '#d1d5db',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: sessionActive ? 'pointer' : 'not-allowed'
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                padding: '1rem',
                border: '1px dashed #d1d5db',
                borderRadius: '0.75rem',
                background: '#f9fafb',
                color: '#6b7280',
                fontSize: '0.875rem'
              }}
            >
              No images uploaded yet. Add reference snapshots or annotated feedback for this submission.
            </div>
          )}
        </div>

        {/* AI Feedback Generator */}
        <div
          style={{
            background: 'white',
            borderRadius: '1rem',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
          <h3 style={{ fontSize: '1.125rem', fontWeight: '700', color: '#111827', marginBottom: '1rem' }}>
            🤖 AI Feedback Generator
          </h3>
          <p style={{ fontSize: '0.9rem', color: '#4b5563', marginBottom: '1rem' }}>
            Generate AI-ready feedback prompts using the rubric selections and student details.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => setAIFeedbackPrompt(generateAIFeedbackPrompt())}
              disabled={!sessionActive}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: sessionActive ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#9ca3af',
                color: 'white',
                padding: '0.75rem 1.5rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: sessionActive ? 'pointer' : 'not-allowed',
                fontSize: '0.9rem',
                fontWeight: '700',
                boxShadow: sessionActive ? '0 2px 8px rgba(59, 130, 246, 0.4)' : 'none'
              }}
            >
              <AlertTriangle size={18} /> Generate AI Prompt
            </button>
            <button
              onClick={exportForAIFeedback}
              disabled={!sessionActive}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: sessionActive ? 'linear-gradient(135deg, #6366f1, #4f46e5)' : '#9ca3af',
                color: 'white',
                padding: '0.75rem 1.5rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: sessionActive ? 'pointer' : 'not-allowed',
                fontSize: '0.9rem',
                fontWeight: '700',
                boxShadow: sessionActive ? '0 2px 8px rgba(99, 102, 241, 0.35)' : 'none'
              }}
            >
              <Download size={18} /> Export AI Prompt
            </button>
            <button
              onClick={copyAIFeedbackPromptToClipboard}
              disabled={!aiFeedbackPrompt}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: aiFeedbackPrompt ? 'linear-gradient(135deg, #22c55e, #16a34a)' : '#9ca3af',
                color: 'white',
                padding: '0.75rem 1.5rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: aiFeedbackPrompt ? 'pointer' : 'not-allowed',
                fontSize: '0.9rem',
                fontWeight: '700',
                boxShadow: aiFeedbackPrompt ? '0 2px 8px rgba(34, 197, 94, 0.35)' : 'none'
              }}
            >
              <Copy size={18} /> Copy AI Prompt
            </button>
          </div>

          {aiFeedbackPrompt && (
            <div
              style={{
                marginTop: '1rem',
                background: 'linear-gradient(135deg, #ede9fe 0%, #e0e7ff 100%)',
                border: '1px solid #c4b5fd',
                borderRadius: '0.75rem',
                padding: '1.25rem'
              }}
            >
              <details open style={{ cursor: 'pointer' }}>
                <summary
                  style={{
                    fontWeight: 700,
                    color: '#4c1d95',
                    fontSize: '1rem',
                    marginBottom: '0.75rem'
                  }}
                >
                  AI feedback prompt ready to copy
                </summary>
                <textarea
                  readOnly
                  value={aiFeedbackPrompt}
                  style={{
                    width: '100%',
                    minHeight: '220px',
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                    background: '#fff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    color: '#111827',
                    fontSize: '0.9rem',
                    lineHeight: 1.5,
                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.08)'
                  }}
                />
              </details>
            </div>
          )}
        </div>
        </main>

        {/* Right Sidebar: Score Summary */}
        <aside style={{ width: '340px', flexShrink: 0 }}>
          <div
            style={{
              position: 'sticky',
              top: '100px',
              maxHeight: 'calc(100vh - 140px)',
              overflowY: 'auto',
              background: 'white',
              borderRadius: '1rem',
              padding: '1.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '2px solid #10b981'
            }}
          >
            <h3
              style={{
                fontSize: '0.875rem',
                fontWeight: '700',
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}
            >
              📊 Score Summary
            </h3>

            <div
              style={{
                background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
                borderRadius: '1rem',
                padding: '1.5rem',
                textAlign: 'center',
                marginBottom: '1rem'
              }}
            >
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                Raw Score
              </div>
              <div
                style={{
                  fontSize: '2.5rem',
                  fontWeight: '700',
                  color: '#3b82f6',
                  lineHeight: 1
                }}
              >
                {Math.round(scoreSummary.rawScore * 10) / 10}
              </div>
              <div style={{ fontSize: '1rem', color: '#6b7280', marginTop: '0.25rem' }}>
                / {rubricTotalPoints}
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div
                style={{
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  color: '#6b7280',
                  marginBottom: '0.75rem',
                  textTransform: 'uppercase'
                }}
              >
                Breakdown
              </div>
              {sharedRubric &&
                sharedRubric.criteria &&
                sharedRubric.criteria.map((criterion) => {
                  const grading = gradingData.rubricGrading[criterion.id];
                  const earnedPoints = calculateLevelPoints({
                    criterion,
                    levelKey: grading?.selectedLevel,
                    rubricLevels: getLevelsForCriterion(criterion)
                  });

                  return (
                    <div
                      key={criterion.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0.75rem 0',
                        borderBottom: '1px solid #f3f4f6'
                      }}
                    >
                      <span style={{ fontSize: '0.875rem', color: '#374151', flex: 1, paddingRight: '0.5rem' }}>
                        {criterion.name}
                      </span>
                      <span
                        style={{
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          color: grading?.selectedLevel ? '#10b981' : '#9ca3af',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {earnedPoints.toFixed(1)} / {criterion.maxPoints}
                      </span>
                    </div>
                  );
                })}
            </div>

            {scoreSummary.penaltyApplied && (
              <div
                style={{
                  padding: '1rem',
                  background: '#fef3c7',
                  borderRadius: '0.75rem',
                  marginBottom: '1rem'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                  <span style={{ color: '#92400e', fontWeight: '600' }}>
                    Late Penalty ({Math.round(scoreSummary.penaltyPercentage * 100)}%)
                  </span>
                  <span style={{ color: '#dc2626', fontWeight: '700' }}>
                    -{(scoreSummary.rawScore * scoreSummary.penaltyPercentage).toFixed(1)}
                  </span>
                </div>
              </div>
            )}

            <div
              style={{
                padding: '1.5rem',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                borderRadius: '1rem',
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}
            >
              <div
                style={{
                  fontSize: '0.875rem',
                  color: 'rgba(255,255,255,0.9)',
                  marginBottom: '0.5rem',
                  fontWeight: '600'
                }}
              >
                FINAL SCORE
              </div>
              <div
                style={{
                  fontSize: '3rem',
                  fontWeight: '700',
                  color: 'white',
                  lineHeight: 1
                }}
              >
                {roundedSummaryScore}
              </div>
              <div
                style={{
                  fontSize: '1.25rem',
                  color: 'rgba(255,255,255,0.9)',
                  marginTop: '0.5rem',
                  fontWeight: '600'
                }}
              >
                {roundedSummaryPercentage}%
              </div>
              <div
                style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1rem',
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: 'white'
                }}
              >
                Letter Grade: {getLetterGrade(roundedSummaryPercentage)}
              </div>
            </div>

            <div
              style={{
                padding: '0.75rem',
                background: '#dcfce7',
                border: '1px solid #10b981',
                borderRadius: '0.5rem',
                textAlign: 'center',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#166534',
                marginBottom: '1rem'
              }}
            >
              ✓ {getGradeStatus(currentStudent?.id) === 'final' ? 'Final Grade Saved' : 'Draft Auto-saved'}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                onClick={handleNextStudentAsFinal}
                disabled={!sessionActive}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: sessionActive ? 'linear-gradient(135deg, #10b981, #059669)' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: sessionActive ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  boxShadow: sessionActive ? '0 2px 8px rgba(16, 185, 129, 0.3)' : 'none'
                }}
              >
                <CheckCircle size={16} />
                Finalize & Next Student
              </button>

              <button
                onClick={handleSaveDraft}
                disabled={!sessionActive}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: sessionActive ? '#f3f4f6' : '#e5e7eb',
                  color: sessionActive ? '#374151' : '#9ca3af',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: sessionActive ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <Save size={16} />
                Save as Draft
              </button>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
};

export default GradingTemplate;
